import React, { useState, useEffect, useRef } from 'react';
import { FaChalkboard, FaRedo, FaRegThumbsUp, FaUser, FaUserTie, FaPlus, FaTrash, FaTimes, FaGripVertical, FaLayerGroup, FaBrain, FaTag, FaInfoCircle } from 'react-icons/fa';
import {  toolNodes, mcpToolNodes } from './NodePalette';
import WorkflowService from '../../services/api/workflow/index';
import { createPortal } from 'react-dom';
import FileUploadComponent from './FileUploadComponent';
import TagInputComponent from './TagInputComponent';
import { isDuplicateAgentTitle, isCustomLlmType } from './workflowUtils';
import customLlmIcon from '../../assets/img/custom-llm.svg';
import AgentAdvancedControls from './AgentAdvancedControls';
import SAModalConfirm from '../common/SAModalConfirm';

const outputTypes = [
    {value: "text", displayName: "Text"},
    {value: "csv", displayName: "CSV"},
    {value: "json", displayName: "JSON"}
];

// ===========================================================================
// WORKFLOW TRIGGERS (schema driven) - everything for the Input node lives here
// To add / change a trigger edit only TRIGGER_OPTIONS below.
// ===========================================================================

// triggerOptions.js
// ---------------------------------------------------------------------------
// Single source of truth for every workflow trigger.
// To add / change a trigger: edit ONLY this file. No JSX changes needed.
//
// Field schema (all optional except key/label/type):
//   key            storage key inside triggerConfig
//   label          label shown above the field
//   type           text | password | number | date | time | datetime-local |
//                  textarea | select | button-group | multi-toggle |
//                  connected-app | hidden
//   options        [{ value, label }]            (select / button-group / multi-toggle)
//   provider       fixed provider for connected-app          e.g. 'OPEN_API'
//   providerFrom   take provider from another field's value  e.g. 'provider'
//   required       must be filled (only checked while the field is visible)
//   optional       shows "(Optional)" next to the label
//   showWhen       { field, equals } | { field, in: [...] } | { field, notEmpty: true }
//   resets         [keys] cleared when this field changes
//   defaultValue   value or () => value
//   span           grid columns out of 6 (default 6 = full width, 3 = half, 2 = third)
//   minFrom        another date field key -> used as "min"
//   placeholder    placeholder text
//
// Trigger-level extras:
//   fields         array of fields above (no fields => default info text)
//   summary(form)  returns a preview string shown under the fields
//   validate(form) returns an error string ('' = valid)
//   legacyKey      also copy the form to node.data[legacyKey] (backward compatibility)
//   toLegacy(form) optional transform for the legacy copy
// ---------------------------------------------------------------------------

const today = () => new Date().toISOString().split('T')[0];
const nowTime = () => new Date().toTimeString().slice(0, 5);
const localTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const range = (from, to) =>
    Array.from({ length: to - from + 1 }, (_, i) => ({ value: from + i, label: String(from + i) }));

const MESSAGING_PROVIDERS = [
    // values must match the ThirdPartyAppsProvider enum on the backend
    { value: 'KAFKA', label: 'Kafka' },
    { value: 'RABBITMQ', label: 'RabbitMQ' },
    { value: 'MQTT', label: 'MQTT' },
];

const dateTimeFields = () => [
    { key: 'startDate', label: 'Start Date', type: 'date', required: true, defaultValue: today, span: 3 },
    { key: 'startTime', label: 'Start Time', type: 'time', required: true, defaultValue: nowTime, span: 3 },
    { key: 'endDate', label: 'End Date', type: 'date', optional: true, minFrom: 'startDate', span: 3 },
    { key: 'endTime', label: 'End Time', type: 'time', optional: true, span: 3 },
];

const endBeforeStart = (f) =>
    f.endDate && f.startDate && f.endDate < f.startDate ? 'End date must be after start date' : '';

const TRIGGER_OPTIONS = [
    // ---------------- Human Initiated ----------------
    { id: 'manual-start', title: 'Manual Start', description: 'Manually trigger the workflow execution', category: 'Human Initiated', icon: 'fa-play-circle', iconColor: '#10B981', gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', cta: 'Enable' },
    { id: 'conversation', title: 'Conversation', description: 'Start workflow through conversational interaction', category: 'Human Initiated', icon: 'fa-comments', iconColor: '#3B82F6', gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)', cta: 'Configure' },

    // ---------------- Goal & Intent ----------------
    {
        id: 'goal-prompt', title: 'Goal Prompt', description: 'Start workflow with a specific goal or objective', category: 'Goal & Intent',
        icon: 'fa-bullseye', iconColor: '#8B5CF6', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', cta: 'Set Goal',
        fields: [{ key: 'goal', label: 'Goal', type: 'textarea', required: true, placeholder: 'Describe the goal for this workflow' }],
    },
    { id: 'api-sdk-trigger', title: 'API / SDK Trigger', description: 'Trigger workflow via API call or SDK integration', category: 'Goal & Intent', icon: 'fa-code', iconColor: '#6366F1', gradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)', cta: 'Configure API' },

    // ---------------- Time-Based ----------------
    {
        id: 'schedule', title: 'Schedule', description: 'Run workflow on a recurring schedule', category: 'Time-Based',
        icon: 'fa-calendar-alt', iconColor: '#EC4899', gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)', cta: 'Set Schedule',
        legacyKey: 'scheduleConfig',
        fields: [
            {
                key: 'scheduleType', label: 'Recurrence Pattern', type: 'button-group', required: true, defaultValue: 'one_time',
                options: [
                    { value: 'one_time', label: 'One Time' }, { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' },
                ],
            },
            ...dateTimeFields(),
            {
                key: 'recurrenceDays', label: 'Select Days', type: 'multi-toggle', required: true, defaultValue: () => [],
                options: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => ({ value: d, label: d })),
                showWhen: { field: 'scheduleType', equals: 'weekly' },
            },
            {
                key: 'recurrenceDates', label: 'Select Dates (1-31)', type: 'multi-toggle', required: true, defaultValue: () => [],
                options: range(1, 31), showWhen: { field: 'scheduleType', equals: 'monthly' },
            },
            { key: 'timezone', type: 'hidden', defaultValue: localTz },
        ],
        validate: endBeforeStart,
        summary: (f) => {
            if (!f.startTime) return '';
            if (f.scheduleType === 'one_time') return f.startDate ? `Runs once on ${f.startDate} at ${f.startTime}` : '';
            if (f.scheduleType === 'daily') return `Runs daily at ${f.startTime}`;
            if (f.scheduleType === 'weekly') return f.recurrenceDays?.length ? `Runs every ${f.recurrenceDays.join(', ')} at ${f.startTime}` : '';
            if (f.scheduleType === 'monthly')
                return f.recurrenceDates?.length ? `Runs monthly on day(s) ${[...f.recurrenceDates].sort((a, b) => a - b).join(', ')} at ${f.startTime}` : '';
            return '';
        },
    },
    {
        id: 'delay', title: 'Delay', description: 'Start workflow after a specified delay', category: 'Time-Based',
        icon: 'fa-hourglass-half', iconColor: '#F59E0B', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', cta: 'Set Delay',
        legacyKey: 'delayConfig',
        toLegacy: (f) => ({
            delayType: f.delayType,
            duration: { hours: Number(f.hours) || 0, minutes: Number(f.minutes) || 0, seconds: Number(f.seconds) || 0 },
            delayUntil: f.delayUntil || '',
        }),
        fields: [
            {
                key: 'delayType', label: 'Delay Type', type: 'button-group', required: true, defaultValue: 'duration',
                options: [{ value: 'duration', label: 'Duration' }, { value: 'datetime', label: 'Until Date/Time' }],
            },
            { key: 'hours', label: 'Hours', type: 'number', min: 0, max: 23, defaultValue: 0, span: 2, showWhen: { field: 'delayType', equals: 'duration' } },
            { key: 'minutes', label: 'Minutes', type: 'number', min: 0, max: 59, defaultValue: 0, span: 2, showWhen: { field: 'delayType', equals: 'duration' } },
            { key: 'seconds', label: 'Seconds', type: 'number', min: 0, max: 59, defaultValue: 0, span: 2, showWhen: { field: 'delayType', equals: 'duration' } },
            { key: 'delayUntil', label: 'Delay Until', type: 'datetime-local', required: true, showWhen: { field: 'delayType', equals: 'datetime' } },
        ],
        validate: (f) =>
            f.delayType === 'duration' && !(Number(f.hours) || Number(f.minutes) || Number(f.seconds)) ? 'Enter a delay duration' : '',
        summary: (f) =>
            f.delayType === 'duration'
                ? `Delay: ${Number(f.hours) || 0}h ${Number(f.minutes) || 0}m ${Number(f.seconds) || 0}s`
                : f.delayUntil ? `Workflow will start on ${new Date(f.delayUntil).toLocaleString()}` : '',
    },

    // ---------------- Event-Based ----------------
    {
        id: 'webhook', title: 'Webhook', description: 'Trigger workflow via HTTP webhook endpoint', category: 'Event-Based',
        icon: 'fa-webhook', iconColor: '#06B6D4', gradient: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)', cta: 'Create Webhook', isWebhook: true,
        fields: [
            {
                key: 'method', label: 'HTTP Method', type: 'select', required: true, defaultValue: 'POST',
                options: ['POST', 'PUT', 'GET'].map((m) => ({ value: m, label: m })),
            },
            { key: 'secret', label: 'Secret', type: 'password', optional: true, placeholder: 'Used to verify incoming requests' },
        ],
    },
    { id: 'tool-event', title: 'Tool Event', description: 'Start workflow when a tool generates an event', category: 'Event-Based', icon: 'fa-wrench', iconColor: '#F97316', gradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)', cta: 'Select Tool' },
    {
        id: 'message-event', title: 'Message Event', description: 'Trigger workflow on incoming message events', category: 'Event-Based',
        icon: 'fa-envelope', iconColor: '#14B8A6', gradient: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)', cta: 'Configure',
        fields: [
            { key: 'provider', label: 'Messaging Provider', type: 'button-group', required: true, options: MESSAGING_PROVIDERS, resets: ['connectedApp'] },
            { key: 'connectedApp', label: 'Connected App', type: 'connected-app', providerFrom: 'provider', required: true, showWhen: { field: 'provider', notEmpty: true } },
        ],
        summary: (f) => (f.provider && f.connectedAppName ? `Listens on ${f.provider} app "${f.connectedAppName}"` : ''),
    },

    // ---------------- Agent & Autonomy ----------------
    { id: 'agent-triggered', title: 'Agent Triggered', description: 'Workflow starts when another agent initiates it', category: 'Agent & Autonomy', icon: 'fa-robot', iconColor: '#8B5CF6', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', cta: 'Link Agent' },
    { id: 'delegated-goal', title: 'Delegated Goal', description: 'Workflow receives a delegated goal from another agent', category: 'Agent & Autonomy', icon: 'fa-handshake', iconColor: '#3B82F6', gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)', cta: 'Set Delegation' },

    // ---------------- Knowledge & Data ----------------
    { id: 'knowledge-update', title: 'Knowledge Update', description: 'Trigger workflow when knowledge base is updated', category: 'Knowledge & Data', icon: 'fa-database', iconColor: '#10B981', gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', cta: 'Monitor Knowledge' },
    { id: 'data-condition', title: 'Data Condition', description: 'Start workflow when specific data conditions are met', category: 'Knowledge & Data', icon: 'fa-filter', iconColor: '#6366F1', gradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)', cta: 'Set Condition' },

    // ---------------- Intelligence Triggers ----------------
    { id: 'self-reflection', title: 'Self-Reflection', description: 'Workflow triggers based on self-assessment and reflection', category: 'Intelligence Triggers', icon: 'fa-brain', iconColor: '#EC4899', gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)', cta: 'Enable Reflection' },
    { id: 'prediction-signal', title: 'Prediction Signal', description: 'Start workflow based on predictive signals and patterns', category: 'Intelligence Triggers', icon: 'fa-chart-line', iconColor: '#F59E0B', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', cta: 'Configure Signal' },
    { id: 'policy-trigger', title: 'Policy Trigger', description: 'Trigger workflow when policy conditions are met', category: 'Intelligence Triggers', icon: 'fa-shield-alt', iconColor: '#06B6D4', gradient: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)', cta: 'Set Policy' },

    // ---------------- Workflow Chaining ----------------
    { id: 'workflow-finished', title: 'Workflow Finished', description: 'Start this workflow when another workflow completes', category: 'Workflow Chaining', icon: 'fa-check-circle', iconColor: '#10B981', gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', cta: 'Link Workflow' },
    { id: 'conditional', title: 'Conditional', description: 'Start workflow based on conditional logic and rules', category: 'Workflow Chaining', icon: 'fa-code-branch', iconColor: '#8B5CF6', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', cta: 'Set Conditions' },

    // ---------------- Dev & Ops ----------------
    { id: 'test-simulation', title: 'Test / Simulation', description: 'Run workflow in test or simulation mode', category: 'Dev & Ops', icon: 'fa-flask', iconColor: '#F97316', gradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)', cta: 'Run Test' },
    { id: 'replay-execution', title: 'Replay Execution', description: 'Replay previous workflow execution', category: 'Dev & Ops', icon: 'fa-redo', iconColor: '#6366F1', gradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)', cta: 'Select Execution' },

    // ---------------- Advanced ----------------
    { id: 'composite-trigger', title: 'Composite Trigger', description: 'Combine multiple triggers with complex logic', category: 'Advanced', icon: 'fa-layer-group', iconColor: '#14B8A6', gradient: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)', cta: 'Build Composite' },
];

// ---------------------------------------------------------------------------
// Generic helpers (work for ANY trigger that has a `fields` schema)
// ---------------------------------------------------------------------------

const resolve = (v) => (typeof v === 'function' ? v() : v);

const isEmptyValue = (v) =>
    v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const isFieldVisible = (field, form) => {
    const c = field.showWhen;
    if (!c) return true;
    const v = form[c.field];
    if (c.notEmpty) return !isEmptyValue(v);
    if ('equals' in c) return v === c.equals;
    if (c.in) return c.in.includes(v);
    return true;
};

// Build the initial form: schema defaults, overridden by the saved config
// (saved config is used only if the node was saved with the same trigger).
const buildInitialForm = (option, nodeData) => {
    if (!option?.fields) return {};
    const form = {};
    option.fields.forEach((f) => {
        form[f.key] = f.defaultValue !== undefined ? resolve(f.defaultValue) : f.type === 'multi-toggle' ? [] : '';
    });
    const saved = nodeData?.triggerId === option.id ? nodeData.triggerConfig : null;
    return { ...form, ...(saved || {}) };
};

// Returns '' when valid, otherwise a human readable reason.
const validateTriggerForm = (option, form) => {
    if (!option?.fields) return '';
    for (const f of option.fields) {
        if (f.required && isFieldVisible(f, form) && isEmptyValue(form[f.key])) {
            return `${f.label || f.key} is required`;
        }
    }
    return option.validate ? option.validate(form) || '' : '';
};

// Only visible fields are saved, so hidden/stale values never reach the backend.
const buildTriggerData = (option, form) => {
    const data = {
        triggerId: option.id,
        triggerType: option.id,
        label: option.title,
        description: option.description,
        triggerConfig: undefined,
    };
    if (!option.fields) return data;

    const config = {};
    option.fields.forEach((f) => {
        if (isFieldVisible(f, form)) {
            config[f.key] = form[f.key];
            if (f.type === 'connected-app') {
                config[`${f.key}Name`] = form[`${f.key}Name`] || '';
                if (form[`${f.key}Raw`]) config[`${f.key}Raw`] = form[`${f.key}Raw`];
            }
        }
    });
    data.triggerConfig = config;
    if (option.legacyKey) data[option.legacyKey] = option.toLegacy ? option.toLegacy(config) : config;
    // The canvas shows the configured provider (RabbitMQ, Kafka, MQTT),
    // not the generic "Message Event" title.
    const providerLabel = MESSAGING_PROVIDERS.find((p) => p.value === config.provider)?.label;
    if (providerLabel) data.label = providerLabel;
    return data;
};

// ---------------------------------------------------------------------------
// Generic field renderer
// ---------------------------------------------------------------------------
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8, display: 'block' };
const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: '1px solid #e5e7eb', fontSize: 14, transition: 'all 0.2s ease',
};

const SENSITIVE = /(password|secret|token|apikey|api_key|credential|sasl|private|jaas)/i;
const HIDDEN_KEYS = new Set(['id', 'userId', 'projectId', 'status', 'createdDate', 'updatedDate', 'name', 'provider']);

// Flattens the saved connected app (up to 2 levels) into [label, value] rows.
// Sensitive values are masked so secrets are never shown in the popup.
const toDetailRows = (raw) => {
    const rows = [];
    const walk = (obj, prefix, depth) => {
        Object.entries(obj || {}).forEach(([k, v]) => {
            if (v === null || v === undefined || v === '' || (depth === 0 && HIDDEN_KEYS.has(k))) return;
            const label = prefix ? `${prefix} / ${k}` : k;
            if (typeof v === 'object' && !Array.isArray(v) && depth < 2) return walk(v, label, depth + 1);
            const value = SENSITIVE.test(k) ? '••••••••' : Array.isArray(v) ? v.join(', ') : String(v);
            rows.push([label.replace(/_/g, ' '), value]);
        });
    };
    walk(raw, '', 0);
    return rows;
};

// Renders the configuration form of ANY trigger from its `fields` schema.
// props:
//   option  - the selected trigger option (from TRIGGER_OPTIONS)
//   form    - current form values
//   setForm - state setter for the form
function TriggerConfigFields({ option, form, setForm }) {
    const [appOptions, setAppOptions] = useState({});   // { [provider]: [{ id, name }] }
    const [appLoading, setAppLoading] = useState({});   // { [provider]: boolean }

    const color = option.iconColor;
    const fields = option.fields || [];

    const providerOf = (f) => f.provider || (f.providerFrom ? form[f.providerFrom] : '');

    // Fetch saved connected apps (Agent Apps) for every visible connected-app field
    useEffect(() => {
        fields
            .filter((f) => f.type === 'connected-app' && isFieldVisible(f, form))
            .forEach((f) => {
                const provider = providerOf(f);
                if (!provider || appOptions[provider] || appLoading[provider]) return;
                setAppLoading((p) => ({ ...p, [provider]: true }));
                WorkflowService.getAuthProvider(String(provider).toUpperCase(), 0, 50)
                    .then((res) => {
                        const content = res?.thirdPartyAppsDTOPage?.content || [];
                        setAppOptions((p) => ({ ...p, [provider]: content.map((i) => ({ id: String(i.id), name: i.name, raw: i })) }));
                    })
                    .catch((err) => {
                        console.error(`Error fetching ${provider} apps:`, err);
                        setAppOptions((p) => ({ ...p, [provider]: [] }));
                    })
                    .finally(() => setAppLoading((p) => ({ ...p, [provider]: false })));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [option.id, form]);

    const setField = (f, value, extra = {}) =>
        setForm((prev) => {
            const next = { ...prev, [f.key]: value, ...extra };
            (f.resets || []).forEach((k) => {
                next[k] = '';
                next[`${k}Name`] = '';
            });
            return next;
        });

    const focus = (e) => {
        e.target.style.borderColor = color;
        e.target.style.boxShadow = `0 0 0 3px ${color}20`;
    };
    const blur = (e) => {
        e.target.style.borderColor = '#e5e7eb';
        e.target.style.boxShadow = 'none';
    };

    const renderControl = (f) => {
        const value = form[f.key] ?? '';

        switch (f.type) {
            case 'button-group':
                return (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {f.options.map((o) => {
                            const active = value === o.value;
                            return (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => setField(f, o.value)}
                                    style={{
                                        flex: '1 1 100px', padding: '12px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
                                        border: `2px solid ${active ? color : '#e5e7eb'}`,
                                        background: active ? `${color}10` : '#fff',
                                        color: active ? color : '#64748b',
                                        fontWeight: active ? 600 : 500,
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                );

            case 'multi-toggle': {
                const selected = Array.isArray(value) ? value : [];
                const toggle = (v) =>
                    setField(f, selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                        {f.options.map((o) => {
                            const active = selected.includes(o.value);
                            return (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => toggle(o.value)}
                                    style={{
                                        minWidth: 40, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                                        border: `2px solid ${active ? color : '#e5e7eb'}`,
                                        background: active ? `${color}15` : '#fff',
                                        color: active ? color : '#64748b',
                                        fontWeight: active ? 600 : 500,
                                    }}
                                >
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                );
            }

            case 'select':
                return (
                    <select
                        className="form-select font13"
                        value={value}
                        onChange={(e) => setField(f, e.target.value)}
                        style={{ borderRadius: 8, padding: '10px 12px' }}
                    >
                        <option value="">Select {String(f.label).toLowerCase()}</option>
                        {f.options.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                );

            case 'connected-app': {
                const provider = providerOf(f);
                const apps = appOptions[provider] || [];
                const loading = !!appLoading[provider];
                const selectedId = value == null || value === '' ? '' : String(value);
                const selectedApp = apps.find((a) => String(a.id) === selectedId);
                const detailRows = toDetailRows(selectedApp?.raw || form[`${f.key}Raw`]);
                return (
                    <>
                        <select
                            className="form-select font13"
                            value={selectedId}
                            disabled={loading || !provider}
                            onChange={(e) => {
                                const app = apps.find((a) => String(a.id) === e.target.value);
                                setField(f, e.target.value, {
                                    [`${f.key}Name`]: app?.name || '',
                                    [`${f.key}Raw`]: app?.raw || null,
                                });
                            }}
                            style={{ borderRadius: 8, padding: '10px 12px' }}
                        >
                            <option value="">{loading ? 'Loading...' : `Select ${provider || ''} app`}</option>
                            {apps.map((a) => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                        </select>
                        {!loading && provider && appOptions[provider] && apps.length === 0 && (
                            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>
                                No {provider} app saved yet. Configure one in Agent Apps first.
                            </div>
                        )}
                        {f.showDetails && selectedId && detailRows.length > 0 && (
                            <div style={{ marginTop: 12, background: '#fff', border: `1px solid ${color}30`, borderRadius: 8, padding: 12 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                    {provider} connection details
                                </div>
                                {detailRows.map(([k, v]) => (
                                    <div key={k} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                                        <span style={{ flex: '0 0 40%', color: '#64748b', textTransform: 'capitalize' }}>{k}</span>
                                        <span style={{ flex: 1, color: '#1e293b', wordBreak: 'break-all' }}>{v}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                );
            }

            case 'textarea':
                return (
                    <textarea
                        value={value}
                        rows={4}
                        placeholder={f.placeholder}
                        onChange={(e) => setField(f, e.target.value)}
                        onFocus={focus}
                        onBlur={blur}
                        style={{ ...inputStyle, resize: 'vertical' }}
                    />
                );

            default: // text, password, number, date, time, datetime-local
                return (
                    <input
                        type={f.type || 'text'}
                        value={value}
                        min={f.minFrom ? form[f.minFrom] || undefined : f.min}
                        max={f.max}
                        placeholder={f.placeholder}
                        onChange={(e) => setField(f, e.target.value)}
                        onFocus={focus}
                        onBlur={blur}
                        style={inputStyle}
                    />
                );
        }
    };

    const summary = option.summary ? option.summary(form) : '';

    return (
        <div style={{ background: '#f8fafc', borderRadius: 12, padding: 24, marginBottom: 24 }}>
            <h4 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '0 0 20px 0' }}>
                {option.title} Configuration
            </h4>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0 16px' }}>
                {fields
                    .filter((f) => f.type !== 'hidden' && isFieldVisible(f, form))
                    .map((f) => (
                        <div key={f.key} style={{ gridColumn: `span ${f.span || 6}`, marginBottom: 20 }}>
                            {f.label && (
                                <label style={labelStyle}>
                                    {f.label}
                                    {f.required && <span style={{ color: '#dc2626' }}> *</span>}
                                    {f.optional && <span style={{ color: '#94a3b8', fontWeight: 400 }}> (Optional)</span>}
                                </label>
                            )}
                            {renderControl(f)}
                        </div>
                    ))}
            </div>

            {summary && (
                <div
                    style={{
                        background: '#fff', borderRadius: 8, padding: 14,
                        border: `1px solid ${color}30`, fontSize: 14, fontWeight: 500, color: '#1e293b',
                    }}
                >
                    {summary}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Trigger cards + popup
// ---------------------------------------------------------------------------
// Webhook icon (Svix style) - used on the card and in the popup header
function WebhookIcon({ size = 24, selected = false }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                position: 'relative',
                zIndex: 1,
                filter: selected ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none',
            }}
        >
            <path d="M7 7C7 7 9 3 12 3C15 3 17 7 17 7" stroke="#EC4899" strokeWidth="2.8" strokeLinecap="round" fill="none" />
            <path d="M7 7C7 7 3 11 3 15C3 17.5 4.5 20 7 20" stroke="#475569" strokeWidth="2.8" strokeLinecap="round" fill="none" />
            <path d="M17 7C17 7 21 11 21 15C21 17.5 19.5 20 17 20" stroke="#475569" strokeWidth="2.8" strokeLinecap="round" fill="none" />
            <path d="M7 20C7 20 9 21 12 21C15 21 17 20 17 20" stroke="#475569" strokeWidth="2.8" strokeLinecap="round" fill="none" />
            <circle cx="7" cy="7" r="2.8" fill="#EC4899" />
            <circle cx="17" cy="7" r="2.8" fill="#EC4899" />
            <circle cx="7" cy="20" r="2.8" fill="#475569" />
            <circle cx="17" cy="20" r="2.8" fill="#475569" />
        </svg>
    );
}

/**
 * "Choose How Your Workflow Starts" panel for the Input node.
 * All trigger definitions live in triggerOptions.js (schema driven).
 *
 * props:
 *   node     - the selected input node
 *   onChange - (newNodeData) => void
 */
function TriggerSelector({ node, onChange }) {
    const [showPopup, setShowPopup] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [triggerForm, setTriggerForm] = useState({});

    // When the popup opens: schema defaults + already saved config for this trigger
    useEffect(() => {
        if (showPopup && selectedOption) {
            setTriggerForm(buildInitialForm(selectedOption, node.data));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPopup, selectedOption?.id]);

    const optionsByCategory = TRIGGER_OPTIONS.reduce((acc, option) => {
        (acc[option.category] = acc[option.category] || []).push(option);
        return acc;
    }, {});

    const selectedTriggerId = node.data.triggerId || null;
    const [messageEventRunning, setMessageEventRunning] = useState(false);
    const [stoppingForConfig, setStoppingForConfig] = useState(false);
    const [stopConfirm, setStopConfirm] = useState(null);
    const pendingConfigChangeRef = useRef(null);
    const applyConfigChangeRef = useRef(() => {});
    const inputLocked = messageEventRunning || stoppingForConfig;

    useEffect(() => {
        const onPodStatus = (event) => {
            const messageEvent = event?.detail?.messageEvent === true;
            const status = event?.detail?.status;
            if (!messageEvent) {
                if (event?.detail?.messageEvent === false) setMessageEventRunning(false);
                return;
            }
            setMessageEventRunning(status === 'Running' || status === 'Stopping');
            if (status === 'Stopped' && pendingConfigChangeRef.current) {
                const pending = pendingConfigChangeRef.current;
                pendingConfigChangeRef.current = null;
                setStoppingForConfig(false);
                applyConfigChangeRef.current(pending);
            }
        };
        const onStopResult = (event) => {
            if (!pendingConfigChangeRef.current) return;
            const status = event?.detail?.status;
            const stillWaiting = event?.detail?.pending && status !== 'Failed';
            if (stillWaiting) return;
            if (event?.detail?.success && status === 'Stopped') {
                const pending = pendingConfigChangeRef.current;
                pendingConfigChangeRef.current = null;
                setStoppingForConfig(false);
                applyConfigChangeRef.current(pending);
                return;
            }
            if (event?.detail?.success) return;
            pendingConfigChangeRef.current = null;
            setStoppingForConfig(false);
        };
        window.addEventListener('workflow-pod-status', onPodStatus);
        window.addEventListener('workflow-pod-status-sync', onPodStatus);
        window.addEventListener('workflow-pod-stop-result', onStopResult);
        window.dispatchEvent(new CustomEvent('sync-workflow-pod-status'));
        return () => {
            window.removeEventListener('workflow-pod-status', onPodStatus);
            window.removeEventListener('workflow-pod-status-sync', onPodStatus);
            window.removeEventListener('workflow-pod-stop-result', onStopResult);
        };
    }, []);

    const openPopup = (option) => {
        setSelectedOption(option);
        setShowPopup(true);
    };

    const closePopup = () => {
        setShowPopup(false);
        setSelectedOption(null);
    };

    // Click on the card itself = select the trigger (config of other triggers is dropped)
    const handleCardSelect = (option) => {
        const keptConfig = node.data.triggerId === option.id ? node.data.triggerConfig : undefined;
        const providerLabel = MESSAGING_PROVIDERS.find((p) => p.value === keptConfig?.provider)?.label;
        onChange({
            ...node.data,
            triggerId: option.id,
            triggerType: option.id,
            // Message Event keeps its own name. The canvas shows the saved
            // provider (Kafka, RabbitMQ, MQTT) only after Save.
            label: option.id === 'message-event' ? (providerLabel || option.title) : option.title,
            description: option.description,
            triggerConfig: keptConfig,
        });
    };

    applyConfigChangeRef.current = (pending) => {
        if (!pending) return;
        if (pending.kind === 'select') handleCardSelect(pending.option);
        if (pending.kind === 'configure') openPopup(pending.option);
        if (pending.kind === 'apply') {
            onChange(pending.data);
            closePopup();
        }
    };

    const requestInputChange = (pending) => {
        if (!inputLocked) {
            applyConfigChangeRef.current(pending);
            return;
        }
        if (stoppingForConfig) return;
        setStopConfirm(pending);
    };

    const confirmStopAndChange = () => {
        const pending = stopConfirm;
        setStopConfirm(null);
        if (!pending) return;
        pendingConfigChangeRef.current = pending;
        setStoppingForConfig(true);
        window.dispatchEvent(new CustomEvent('stop-workflow-pod'));
    };

    const triggerError = selectedOption ? validateTriggerForm(selectedOption, triggerForm) : '';

    const handleConfirm = () => {
        if (!selectedOption || triggerError) return;
        const nextData = { ...node.data, ...buildTriggerData(selectedOption, triggerForm) };
        if (inputLocked) {
            requestInputChange({ kind: 'apply', data: nextData });
            return;
        }
        onChange(nextData);
        closePopup();
    };

    return (
        <div style={{ height: '100%', overflowY: 'auto', background: 'linear-gradient(to bottom, #f8fafc 0%, #ffffff 100%)' }}>
            <style>{`
        @keyframes shine {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
      `}</style>

            {/* Page Header */}
            <div
                style={{
                    padding: '10px',
                    background: '#ffffff',
                    borderBottom: '1px solid #e5e7eb',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
            >
                <h3 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: '0 0 8px 0', letterSpacing: '-0.02em' }}>
                    Choose How Your Workflow Starts
                </h3>
                <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>Define when intelligence enters the system.</p>
                {inputLocked && (
                    <p style={{ fontSize: 13, color: '#b45309', margin: '8px 0 0 0' }}>
                        Input configuration is locked while this message event is running.
                    </p>
                )}
            </div>

            {/* Options by Category */}
            <div style={{ padding: 20, opacity: inputLocked ? 0.72 : 1 }}>
                {Object.entries(optionsByCategory).map(([category, options]) => (
                    <div key={category} style={{ marginBottom: 32 }}>
                        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #e5e7eb' }}>
                            <h3
                                style={{
                                    fontWeight: 600,
                                    color: '#334155',
                                    margin: 0,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontSize: 12,
                                }}
                            >
                                {category}
                            </h3>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                            {options.map((option) => {
                                const isSelected = selectedTriggerId === option.id;
                                return (
                                    <div
                                        key={option.id}
                                        onClick={() => {
                                            if (option.id === selectedTriggerId) return;
                                            requestInputChange({ kind: 'select', option });
                                        }}
                                        style={{
                                            background: isSelected
                                                ? `linear-gradient(135deg, ${option.iconColor}08 0%, ${option.iconColor}03 100%)`
                                                : '#ffffff',
                                            border: `2px solid ${isSelected ? option.iconColor : '#e5e7eb'}`,
                                            borderRadius: 12,
                                            padding: 20,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            position: 'relative',
                                            boxShadow: isSelected
                                                ? `0 4px 16px ${option.iconColor}25, 0 2px 8px ${option.iconColor}15`
                                                : '0 1px 3px rgba(0,0,0,0.05)',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.borderColor = `${option.iconColor}60`;
                                                e.currentTarget.style.boxShadow = `0 4px 12px ${option.iconColor}20, 0 2px 6px rgba(0,0,0,0.08)`;
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.background = `linear-gradient(135deg, ${option.iconColor}05 0%, #ffffff 100%)`;
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.background = '#ffffff';
                                            }
                                        }}
                                    >
                                        {/* Icon */}
                                        <div
                                            style={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: 14,
                                                background: isSelected
                                                    ? option.gradient
                                                    : `linear-gradient(135deg, ${option.iconColor}15 0%, ${option.iconColor}08 100%)`,
                                                border: isSelected ? `2px solid ${option.iconColor}` : `2px solid ${option.iconColor}20`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: 16,
                                                transition: 'all 0.3s ease',
                                                boxShadow: isSelected ? `0 4px 12px ${option.iconColor}40` : '0 2px 4px rgba(0,0,0,0.05)',
                                                position: 'relative',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {isSelected && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: '-50%',
                                                        left: '-50%',
                                                        width: '200%',
                                                        height: '200%',
                                                        background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)',
                                                        animation: 'shine 3s infinite',
                                                        pointerEvents: 'none',
                                                    }}
                                                />
                                            )}
                                            {option.isWebhook ? (
                                                <WebhookIcon size={24} selected={isSelected} />
                                            ) : (
                                                <i
                                                    className={`fas ${option.icon}`}
                                                    style={{
                                                        fontSize: 24,
                                                        color: isSelected ? '#ffffff' : option.iconColor,
                                                        position: 'relative',
                                                        zIndex: 1,
                                                        filter: isSelected ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none',
                                                    }}
                                                />
                                            )}
                                        </div>

                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '0 0 8px 0', lineHeight: 1.4 }}>
                                            {option.title}
                                        </h4>
                                        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px 0', lineHeight: 1.5 }}>
                                            {option.description}
                                        </p>

                                        {/* CTA + selected check */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                                            <a
                                                href="#"
                                                className="trigger-cta-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    requestInputChange({ kind: 'configure', option });
                                                }}
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: option.iconColor,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.textDecoration = 'underline';
                                                    e.target.style.opacity = '0.8';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.textDecoration = 'none';
                                                    e.target.style.opacity = '1';
                                                }}
                                            >
                                                {option.cta}
                                            </a>
                                            {isSelected && (
                                                <div
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: '50%',
                                                        background: option.gradient,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: `0 2px 8px ${option.iconColor}40`,
                                                    }}
                                                >
                                                    <i className="fas fa-check" style={{ color: '#ffffff', fontSize: 12 }} />
                                                </div>
                                            )}
                                        </div>

                                        {isSelected && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 16,
                                                    right: 16,
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    background: option.gradient,
                                                    boxShadow: `0 0 0 4px ${option.iconColor}20, 0 0 0 8px ${option.iconColor}10`,
                                                    animation: 'pulse 2s infinite',
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Trigger configuration popup */}
            {showPopup &&
                selectedOption &&
                createPortal(
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999,
                            padding: 20,
                        }}
                        onClick={closePopup}
                    >
                        <div
                            style={{
                                background: '#ffffff',
                                borderRadius: 16,
                                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                maxWidth: 600,
                                width: '100%',
                                maxHeight: '90vh',
                                overflow: 'auto',
                                position: 'relative',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    padding: '24px 24px 20px 24px',
                                    borderBottom: '1px solid #e5e7eb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    background: `linear-gradient(135deg, ${selectedOption.iconColor}08 0%, #ffffff 100%)`,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div
                                        style={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 14,
                                            background: selectedOption.gradient,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: `0 4px 12px ${selectedOption.iconColor}30`,
                                        }}
                                    >
                                        {selectedOption.isWebhook ? (
                                            <WebhookIcon size={28} />
                                        ) : (
                                            <i className={`fas ${selectedOption.icon}`} style={{ fontSize: 28, color: '#ffffff' }} />
                                        )}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0' }}>
                                            {selectedOption.title}
                                        </h3>
                                        <p
                                            style={{
                                                fontSize: 12,
                                                color: '#64748b',
                                                margin: 0,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {selectedOption.category}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={closePopup}
                                    style={{
                                        background: '#f1f5f9',
                                        border: 'none',
                                        borderRadius: 8,
                                        width: 32,
                                        height: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: '#64748b',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = '#e2e8f0')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                                >
                                    <i className="fas fa-times" style={{ fontSize: 16 }} />
                                </button>
                            </div>

                            {/* Body */}
                            <div style={{ padding: 24 }}>
                                <p style={{ fontSize: 15, color: '#475569', lineHeight: 1.6, margin: '0 0 24px 0' }}>
                                    {selectedOption.description}
                                </p>

                                {selectedOption.fields ? (
                                    <TriggerConfigFields option={selectedOption} form={triggerForm} setForm={setTriggerForm} />
                                ) : (
                                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: 20, marginBottom: 24 }}>
                                        <h4
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: '#334155',
                                                margin: '0 0 12px 0',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            Configuration Details
                                        </h4>
                                        <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
                                            Configure this trigger to start your workflow when the specified condition is met.
                                        </div>
                                    </div>
                                )}

                                {triggerError && (
                                    <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12, textAlign: 'right' }}>
                                        {triggerError}
                                    </div>
                                )}

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={closePopup}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: 8,
                                            border: '1px solid #e5e7eb',
                                            background: '#ffffff',
                                            color: '#64748b',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirm}
                                        disabled={!!triggerError}
                                        title={triggerError || ''}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: 8,
                                            border: 'none',
                                            background: selectedOption.gradient,
                                            color: '#ffffff',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            cursor: triggerError ? 'not-allowed' : 'pointer',
                                            opacity: triggerError ? 0.5 : 1,
                                            boxShadow: `0 4px 12px ${selectedOption.iconColor}40`,
                                        }}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            <SAModalConfirm
                show={Boolean(stopConfirm)}
                title="Stop workflow"
                message="This message event is running. Stop it and change the input configuration?"
                okText="Stop and change"
                cancelText="Cancel"
                handleOk={confirmStopAndChange}
                handleCancel={() => setStopConfirm(null)}
            />
        </div>
    );
}

// ===========================================================================

export default function NodeConfigSidebar({ node, onChange, llmNodes, nodes, edges }) {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [modalValue, setModalValue] = useState('');
    const [toolConfigType, setToolConfigType] = useState('system');
    const [dynamicSelectOptions, setDynamicSelectOptions] = useState({});
    const [showPassword, setShowPassword] = useState({}); // Track password visibility for each field
    const [jsonFieldErrors, setJsonFieldErrors] = useState({}); // { [attrKey]: errorMessage }

    // Task node specific hooks - must be at top level
    const [instructionGroups, setInstructionGroups] = useState(() => {
        if (node?.type === 'task' && node?.data?.instructionGroups && Array.isArray(node.data.instructionGroups)) {
            return node.data.instructionGroups;
        }
        return [];
    });

    const [methodParams, setMethodParams] = useState(() => {
        if (node?.type === 'task') {
            return node.data?.methodParams || {};
        }
        return {};
    });

    // Track expanded methods for task nodes
    const [expandedMethods, setExpandedMethods] = useState(new Set());

    // Helper: check if current agent node is in a parallel group (same source node with other agents)
    const isAgentInParallelGroup = () => {
        if (!node || node.type !== 'agent' || !Array.isArray(nodes) || !Array.isArray(edges)) {
            return false;
        }

        const incoming = edges.filter(e => e.target === node.id);

        if (incoming.length === 0) return false;

        for (const inEdge of incoming) {
            const { source } = inEdge;

            // All non-tool/LLM edges from the same source node
            const groupEdges = edges.filter(
                e =>
                    e.source === source &&
                    e.sourceHandle !== 'tool' &&
                    e.sourceHandle !== 'chatModel'
            );

            const agentIds = new Set();
            groupEdges.forEach(edge => {
                const targetNode = nodes.find(n => n.id === edge.target);
                if (targetNode && targetNode.type === 'agent') {
                    agentIds.add(targetNode.id);
                }
            });

            if (agentIds.size > 1 && agentIds.has(node.id)) {
                return true;
            }
        }

        return false;
    };

    // Helper: get only the agents that are parallel (share same source as current agent)
    const getParallelAgentsForCurrent = () => {
        if (!node || node.type !== 'agent' || !Array.isArray(nodes) || !Array.isArray(edges)) {
            return [];
        }

        const incoming = edges.filter(e => e.target === node.id);
        if (incoming.length === 0) return [];

        const parallelIds = new Set();

        incoming.forEach(inEdge => {
            const { source } = inEdge;

            const groupEdges = edges.filter(
                e =>
                    e.source === source &&
                    e.sourceHandle !== 'tool' &&
                    e.sourceHandle !== 'chatModel'
            );

            groupEdges.forEach(edge => {
                const targetNode = nodes.find(n => n.id === edge.target);
                if (targetNode && targetNode.type === 'agent') {
                    parallelIds.add(targetNode.id);
                }
            });
        });

        // Only return when there is an actual parallel group (2+ agents)
        if (parallelIds.size < 2 || !parallelIds.has(node.id)) {
            return [];
        }

        return nodes.filter(n => n.type === 'agent' && parallelIds.has(n.id));
    };

    // Show Intelligence Decision section automatically for parallel agents,
    // but leave the checkbox selection entirely to the user
    useEffect(() => {
        if (!node || node.type !== 'agent') return;
        if (!isAgentInParallelGroup()) return;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node?.id, node?.type, nodes, edges]);

    // Build instruction structure helper (used by useEffect)
    const buildInstructionStructure = (groups) => {
        const result = [];

        // Helper function to process parameters and convert types appropriately
        const processParams = (params, methodName) => {
            if (!params || typeof params !== 'object') return params;

            const processedParams = { ...params };
            const methodInfo = node?.data?.methodDescriptions?.[methodName] || {};
            const paramInfoList = methodInfo.parameters || [];

            // Process each parameter
            Object.keys(processedParams).forEach(paramKey => {
                const paramInfo = paramInfoList.find(p =>
                    (typeof p === 'string' ? p : p.name) === paramKey
                );
                const paramType = typeof paramInfo === 'object' ? paramInfo.type : null;
                const paramValue = processedParams[paramKey];

                // Skip if value is empty or undefined
                if (paramValue === undefined || paramValue === null || paramValue === '') {
                    // For optional parameters, we might want to remove them, but for now keep empty strings
                    return;
                }

                // Convert based on parameter type
                if (paramType === 'file') {
                    // If parameter is of type 'file', parse JSON string to array
                    try {
                        const fileValue = paramValue;
                        if (!fileValue || (typeof fileValue === 'string' && !fileValue.trim())) {
                            // Empty value - set to empty array
                            processedParams[paramKey] = [];
                        } else if (typeof fileValue === 'string' && fileValue.trim()) {
                            const parsed = JSON.parse(fileValue);
                            // Convert to array of file objects
                            if (Array.isArray(parsed)) {
                                processedParams[paramKey] = parsed.map(file => ({
                                    name: file.name,
                                    size: file.size,
                                    type: file.type,
                                    base64: file.base64,
                                    dataUrl: file.dataUrl
                                }));
                            } else if (parsed && typeof parsed === 'object') {
                                // Single file object - convert to array
                                processedParams[paramKey] = [{
                                    name: parsed.name,
                                    size: parsed.size,
                                    type: parsed.type,
                                    base64: parsed.base64,
                                    dataUrl: parsed.dataUrl
                                }];
                            } else {
                                processedParams[paramKey] = [];
                            }
                        } else if (Array.isArray(fileValue)) {
                            // Already an array - ensure proper structure
                            processedParams[paramKey] = fileValue.map(file => ({
                                name: file.name,
                                size: file.size,
                                type: file.type,
                                base64: file.base64,
                                dataUrl: file.dataUrl
                            }));
                        } else {
                            // Invalid format - set to empty array
                            processedParams[paramKey] = [];
                        }
                    } catch (e) {
                        // If parsing fails, set to empty array
                        console.warn(`Failed to parse file parameter ${paramKey}:`, e);
                        processedParams[paramKey] = [];
                    }
                } else if (paramType === 'number') {
                    // Convert string to number
                    if (typeof paramValue === 'string' && paramValue.trim() !== '') {
                        const numValue = Number(paramValue);
                        // Check if conversion was successful (not NaN)
                        if (!isNaN(numValue)) {
                            processedParams[paramKey] = numValue;
                        }
                        // If NaN, keep original value (might be intentional)
                    } else if (typeof paramValue === 'number') {
                        // Already a number, keep as is
                        processedParams[paramKey] = paramValue;
                    }
                } else if (paramType === 'boolean') {
                    // Convert string to boolean
                    if (typeof paramValue === 'string') {
                        const lowerValue = paramValue.toLowerCase().trim();
                        if (lowerValue === 'true' || lowerValue === '1' || lowerValue === 'yes') {
                            processedParams[paramKey] = true;
                        } else if (lowerValue === 'false' || lowerValue === '0' || lowerValue === 'no' || lowerValue === '') {
                            processedParams[paramKey] = false;
                        }
                        // If not a recognized boolean string, keep original
                    } else if (typeof paramValue === 'boolean') {
                        // Already a boolean, keep as is
                        processedParams[paramKey] = paramValue;
                    }
                } else if (paramType === 'tag') {
                    // Convert tag JSON string to array
                    try {
                        if (!paramValue || (typeof paramValue === 'string' && !paramValue.trim())) {
                            // Empty value - set to empty array
                            processedParams[paramKey] = [];
                        } else if (typeof paramValue === 'string' && paramValue.trim()) {
                            const parsed = JSON.parse(paramValue);
                            // Ensure it's an array of strings
                            if (Array.isArray(parsed)) {
                                processedParams[paramKey] = parsed.filter(tag => tag && typeof tag === 'string' && tag.trim());
                            } else {
                                processedParams[paramKey] = [];
                            }
                        } else if (Array.isArray(paramValue)) {
                            // Already an array - ensure it's an array of strings
                            processedParams[paramKey] = paramValue.filter(tag => tag && typeof tag === 'string' && tag.trim());
                        } else {
                            // Invalid format - set to empty array
                            processedParams[paramKey] = [];
                        }
                    } catch (e) {
                        // If parsing fails, set to empty array
                        console.warn(`Failed to parse tag parameter ${paramKey}:`, e);
                        processedParams[paramKey] = [];
                    }
                }
                // For 'text', 'textarea', and other types, keep as string (default behavior)
            });

            return processedParams;
        };

        groups.forEach(group => {
            if (group.type === 'parallel') {
                // Parallel group: array of method objects
                const parallelMethods = group.methods.map(m => {
                    const methodObj = {};
                    methodObj[m.name] = processParams(m.params || {}, m.name);
                    return methodObj;
                });
                if (parallelMethods.length > 0) {
                    result.push(parallelMethods);
                }
            } else {
                // Sequence: individual method objects
                group.methods.forEach(m => {
                    const methodObj = {};
                    methodObj[m.name] = processParams(m.params || {}, m.name);
                    result.push(methodObj);
                });
            }
        });

        return result;
    };

    // Sync instructionGroups when node changes (for task nodes)
    useEffect(() => {
        if (node?.type === 'task') {
            // Check if any tools are connected
            const connectedTools = node.data?.connectedConfig?.tool || [];
            const hasToolsConnected = connectedTools.length > 0;

            // If no tools connected, set empty instructionGroups
            if (!hasToolsConnected) {
                setInstructionGroups([]);
                setMethodParams({});
            } else {
                // Tools are connected, sync instructionGroups from node data
                if (node.data?.instructionGroups && Array.isArray(node.data.instructionGroups)) {
                    setInstructionGroups(node.data.instructionGroups);
                } else {
                    setInstructionGroups([]);
                }
                setMethodParams(node.data?.methodParams || {});
            }
        } else {
            // Reset when not a task node
            setInstructionGroups([]);
            setMethodParams({});
            setExpandedMethods(new Set());
        }
    }, [node?.id, node?.type, node?.data?.connectedConfig?.tool]);

    // Update node data when instructions change (only for task nodes)
    useEffect(() => {
        if (node?.type === 'task') {
            const builtInstructions = buildInstructionStructure(instructionGroups);
            onChange({
                ...node.data,
                instructions: builtInstructions,
                instructionGroups: instructionGroups,
                methodParams: methodParams
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instructionGroups, methodParams, node?.type, node?.id]);

    // Fetch dynamic options for select fields when provider exists
    useEffect(() => {
        if (node?.type === 'config' && node?.data?.configType === 'tool' && node?.data?.provider) {
            let selectedTool = null;
            if(node.data.handlerName === 'MCPHandler') {
                selectedTool = node.data;
            } else {
                selectedTool = toolNodes.find(t => t.handlerName === node.data.handlerName);
            }
            if (selectedTool?.attributes) {
                selectedTool.attributes.forEach((attr) => {
                    if (attr.type === 'select' && !attr.options && !dynamicSelectOptions[attr.key]) {
                        // Fetch options from API
                        WorkflowService.getAuthProvider(node.data.provider.toUpperCase(), 0, 10)
                            .then((response) => {
                                if (response?.thirdPartyAppsDTOPage) {
                                    const options = response?.thirdPartyAppsDTOPage?.content?.map((item) => ({
                                        value: item.id,
                                        label: item.name
                                    }));
                                    if (options.length > 0) {
                                        setDynamicSelectOptions(prev => ({
                                            ...prev,
                                            [attr.key]: options
                                        }));
                                    }
                                }
                            })
                            .catch((error) => {
                                console.error(`Error fetching options for ${attr.key}:`, error);
                            });
                    }
                });
            }
        }
    }, [node?.type, node?.data?.configType, node?.data?.provider, node?.id, node?.data?.handlerName]);

    if (!node) return <div className="p-3 text-muted">Select a node to configure</div>;

    // Input Node Configuration (schema driven, see TRIGGER_OPTIONS at top of file)
    if (node.type === 'input') {
        return <TriggerSelector node={node} onChange={onChange} />;
    }

    // Tool Configuration
    if (node.type === 'config' && node.data.configType === 'tool') {
        let selectedTool = null;
        if(node.data.handlerName === 'MCPHandler') {
            selectedTool = node.data
        } else {
            selectedTool = toolNodes.find(t => t.handlerName === node.data.handlerName);
        }

        if (!selectedTool) return <div className="p-3 text-muted">Tool configuration not found</div>;

        // Helper function to get attribute value (handles both object and primitive formats)
        const getAttributeValue = (key) => {
            const attrValue = node.data.attributes?.[key];
            if (attrValue === null || attrValue === undefined) return '';
            // If it's an object with value property, extract the value
            if (typeof attrValue === 'object' && attrValue !== null && 'value' in attrValue) {
                const value = attrValue.value;
                // Handle boolean false and 0 explicitly (they are falsy but valid values)
                if (value === false || value === 0) return value;
                return value || '';
            }
            // Otherwise return as-is (backward compatibility)
            return attrValue;
        };

        // Helper function to strip surrounding quotes from password values
        const stripQuotes = (value) => {
            if (typeof value === 'string' && value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
                return value.slice(1, -1);
            }
            return value;
        };

        // Handle attribute changes
        const handleAttributeChange = (attr, value) => {
            const currentAttributes = node.data.attributes || {};
            let parsedValue = value;
            if(attr?.type === "number") {
                parsedValue = Number(value);
            } else if (attr?.type === "json") {
                // Store JSON as raw string (not wrapped in extra quotes)
                parsedValue = value;
            } else if(attr?.type === "text" || attr?.type === "password") {
                parsedValue = value;
            } else if(attr?.type === "select" || attr?.type === "l_select") {
                // Select values should be stored as-is, without quotes
                parsedValue = typeof value === 'string' ? value : value?.value;
            } else {
                parsedValue = `"${value}"`
            }
            const hasOauth = attr?.source === "oauth" ? true : false;

            // For provider-backed selects, ensure `id` is the selected id (value),
            // and `name` is the human-readable label.
            const optionsForAttr = attr?.options || dynamicSelectOptions?.[attr?.key] || [];
            const resolvedOption =
                typeof value === 'string'
                    ? optionsForAttr.find((opt) => String(opt?.value) === String(parsedValue)) || null
                    : value || null;
            const resolvedName =
                resolvedOption?.name != null ? resolvedOption.name : resolvedOption?.label != null ? resolvedOption.label : null;

            const attributeValue = {
                value: parsedValue,
                hasOauth: hasOauth,
                provider: hasOauth ? node.data.provider : null,
                id: parsedValue,
                name: resolvedName ?? parsedValue
            };
            const updatedAttributes = {
                ...currentAttributes,
                [attr?.key]: attributeValue
            };
            onChange({
                ...node.data,
                attributes: updatedAttributes
            });
        };

        // Handle select click - fetch options if provider exists
        const handleSelectClick = (attr) => {
            // Only fetch if provider exists and options haven't been loaded yet
            if (node.data.provider && attr.type === 'select' && !attr.options && !dynamicSelectOptions[attr.key]) {
                WorkflowService.getAuthProvider(node.data.provider.toUpperCase(), 0, 10)
                    .then((response) => {
                        if (response?.thirdPartyAppsDTOPage) {
                            const options = response?.thirdPartyAppsDTOPage?.content?.map((item) => ({
                                value: String(item.id),
                                label: item.name,
                                id: item.id,
                                name: item.name
                            }));
                            if (options.length > 0) {
                                setDynamicSelectOptions(prev => ({
                                    ...prev,
                                    [attr.key]: options
                                }));
                            }
                        }
                    })
                    .catch((error) => {
                        console.error(`Error fetching options for ${attr.key}:`, error);
                    });
            }
        };


        const handleAttributeChangeMCP = (attr, value) => {
            const parsedValue =
                attr?.type === "number" ? Number(value) :
                    attr?.type === "text" || attr?.type === "password" ? value :
                        `"${value}"`;

            const attributes = node.data.attributes;

            // Case 1: attributes is an ARRAY (Mcp Environment structure)
            if (Array.isArray(attributes)) {
                const updatedAttributes = attributes.map(item => {
                    if (item.key === "Mcp Environment") {
                        const updatedEnv = item.value.map(envItem =>
                            envItem.key === attr.key
                                ? { ...envItem, value: parsedValue }
                                : envItem
                        );

                        return { ...item, value: updatedEnv };
                    }
                    return item;
                });
                onChange({
                    ...node.data,
                    attributes: updatedAttributes
                });
                return;
            }

            // Case 2: attributes is an OBJECT (normal key/value update)
            const updatedAttributes = {
                ...attributes,
                [attr.key]: parsedValue
            };

            onChange({
                ...node.data,
                attributes: updatedAttributes
            });
        };
        // Get available LLM nodes from the workflow
        const availableLLMs = nodes.filter(n => n.type === 'config' && n.data.configType === 'llm');

        const uniqueLLMs = Array.from(
            new Map(
                availableLLMs.map(llm => [
                    `${llm.data.displayName}_${llm.data.model}`, // unique key
                    llm
                ])
            ).values()
        );

        return (
            <div style={{ margin: 12, minWidth: 320 }}>
                <div style={{ padding: 5 }}>
                    {/* Tool Display Name */}
                    <div style={{
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: 12,
                        padding: '16px',
                        marginBottom: 24
                    }}>
                        <label className="font14" style={{
                            color: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 12
                        }}>
                            <i className="fa-solid fa-tag" style={{ color: selectedTool.iconColor }}></i>
                            <b>Display Name</b>
                        </label>
                        <input
                            type="text"
                            className={`form-control font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes('display name') ? ' is-invalid' : ''}`}
                            value={node.data.displayName || selectedTool.displayName}
                            onChange={(e) => {
                                const newDisplayName = e.target.value;

                                const hasConflict = nodes.some(n =>
                                    n.type === 'config' &&
                                    n.data?.configType === 'tool' &&
                                    n.id !== node.id &&
                                    (n.data?.displayName || n.data?.handlerName) === newDisplayName
                                );

                                onChange({
                                    ...node.data,
                                    displayName: newDisplayName,
                                    errorMessage: hasConflict
                                        ? 'Display name is already used by another tool. Please choose a unique name.'
                                        : ''
                                });
                            }}
                            placeholder="Enter display name"
                            style={{
                                borderRadius: 8,
                                padding: '8px 12px',
                                // border: '1.5px solid #e1e5e9',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        />
                        {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes('display name') && (
                            <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 6 }}>
                                {node.data.errorMessage}
                            </div>
                        )}
                    </div>

                    {/* Configuration Section */}
                    <div style={{ marginBottom: 20 }}>
                        <label className="font13" style={{
                            color: '#4a5568',
                            marginBottom: 12,
                            display: 'block',
                            fontWeight: 500
                        }}>
                            Configuration Type
                        </label>
                        <div style={{ display: 'flex', gap: 12 }}>

                            {/* Env Config Option */}
                            <label style={{
                                flex: 1,
                                cursor: 'pointer',
                                position: 'relative'
                            }}>
                                <input
                                    type="radio"
                                    name="configType"
                                    value="env"
                                    checked={toolConfigType === 'env'}
                                    onClick={() => setToolConfigType('env')}
                                    style={{ display: 'none' }}
                                />
                                <div style={{
                                    padding: '16px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: 12,
                                    background: toolConfigType === 'env' ? '#f0f9ff' : '#fff',
                                    borderColor: toolConfigType === 'env' ? '#3b82f6' : '#e2e8f0',
                                    transition: 'all 0.2s ease',
                                    position: 'relative'
                                }}>
                                    {toolConfigType === 'env' && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 5,
                                            right: 7,
                                            width: 20,
                                            height: 20,
                                            background: '#3b82f6',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            fontSize: 12
                                        }}>
                                            <i className="fa-solid fa-check"></i>
                                        </div>
                                    )}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        marginBottom: 8
                                    }}>
                                        <i className="fa-solid fa-shield-alt" style={{
                                            color: toolConfigType === 'env' ? '#3b82f6' : '#64748b',
                                            fontSize: 16
                                        }}></i>
                                        <span style={{
                                            fontWeight: 600,
                                            color: toolConfigType === 'env' ? '#1e40af' : '#374151'
                                        }}>
                          Env Config
                        </span>
                                    </div>
                                    <p style={{
                                        fontSize: 12,
                                        color: '#6b7280',
                                        margin: 0,
                                        lineHeight: 1.4
                                    }}>
                                        Use environment variables for sensitive data. Values are stored securely and not shared in the workflow.
                                    </p>
                                </div>
                            </label>

                            {/* System Config Option */}
                            <label style={{
                                flex: 1,
                                cursor: 'pointer',
                                position: 'relative'
                            }}>
                                <input
                                    type="radio"
                                    name="configType"
                                    value="system"
                                    checked={!toolConfigType || toolConfigType === 'system'}
                                    onClick={() => setToolConfigType('system')}
                                    style={{ display: 'none' }}
                                />
                                <div style={{
                                    padding: '16px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: 12,
                                    background: (!toolConfigType || toolConfigType === 'system') ? '#f0f9ff' : '#fff',
                                    borderColor: (!toolConfigType || toolConfigType === 'system') ? '#3b82f6' : '#e2e8f0',
                                    transition: 'all 0.2s ease',
                                    position: 'relative'
                                }}>
                                    {(!toolConfigType || toolConfigType === 'system') && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 5,
                                            right: 7,
                                            width: 20,
                                            height: 20,
                                            background: '#3b82f6',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            fontSize: 12
                                        }}>
                                            <i className="fa-solid fa-check"></i>
                                        </div>
                                    )}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        marginBottom: 8
                                    }}>
                                        <i className="fa-solid fa-server" style={{
                                            color: (!toolConfigType || toolConfigType === 'system') ? '#3b82f6' : '#64748b',
                                            fontSize: 16
                                        }}></i>
                                        <span style={{
                                            fontWeight: 600,
                                            color: (!toolConfigType || toolConfigType === 'system') ? '#1e40af' : '#374151'
                                        }}>
                          System Config
                        </span>
                                    </div>
                                    <p style={{
                                        fontSize: 12,
                                        color: '#6b7280',
                                        margin: 0,
                                        lineHeight: 1.4
                                    }}>
                                        Store configuration in the workflow. Values are saved with your workflow and shared with other users.
                                    </p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* key value pair add and remove  */}
                    {toolConfigType === 'env' && <div style={{ marginBottom: 20 }}>
                        {selectedTool?.attributes && selectedTool?.attributes?.length > 0 &&
                            selectedTool.attributes.some(item => item.env_key).length > 0 &&
                            <label className="font13 required mb-1 font-weight-bold">
                                Environment Variables
                            </label>}

                        <div className='env-var-main' id='wiz-first'>
                            {selectedTool.attributes.map((item, index) => (
                                item.env_key && <div key={index} className="py-3 mb-1" style={{ marginRight: 10 }}>
                                    <div className="align-items-center" style={{ boxShadow: '0 1px 2px rgba(239, 69, 69, 0.05)', padding: '10px', border: '1px solid #e9ecef', borderRadius: 10 }}>
                                        <div className="mb-3 env-key">
                                            <input
                                                type="text"
                                                className={
                                                    "form-control input-md force-non-edit"
                                                }
                                                autoComplete="one-time-code"
                                                placeholder="Key"
                                                id={`floatingInputKey-${index}`}
                                                value={item.env_key || ''}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            {item.type === 'select' ? (
                                                <select
                                                    className={`form-select input-md ${!item.value && item.touched ? 'is-invalid' : ''}`}
                                                    autoComplete="one-time-code"
                                                    id={`floatingInputVal-${index}`}
                                                    style={{
                                                        borderRadius: 8,
                                                        padding: '8px 12px',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                        background: '#fff'
                                                    }}
                                                >
                                                    <option value="">Select {item.env_key?.split('_').map(word => word.toLowerCase()).join(' ') || 'Value'}</option>
                                                    {(item.options || dynamicSelectOptions[item.key] || []).map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    className={`form-control input-md ${!item.value && item.touched ? 'is-invalid' : ''}`}
                                                    autoComplete="one-time-code"
                                                    placeholder='Value'
                                                    id={`floatingInputVal-${index}`}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    }


                    {selectedTool.attributes && selectedTool.attributes.length > 0 && toolConfigType === 'system' && (
                        <div style={{
                            background: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: 12,
                            padding: '16px',
                            marginBottom: 16
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 16,
                                color: '#2c3e50',
                                fontWeight: 600
                            }}>
                                <i className="fa-solid fa-gear" style={{ color: selectedTool.iconColor }}></i>
                                <span>Configuration</span>
                            </div>

                            {selectedTool.attributes.map((attr, index) => (
                                <div key={attr.key} className="form-group" style={{
                                    marginBottom: index === selectedTool.attributes.length - 1 ? 0 : 16
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: 8
                                    }}>
                                        <label className={attr.required ? 'font13 required' : 'font13'} style={{
                                            color: '#4a5568',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            margin: 0
                                        }}>
                                            <i className="fa-solid fa-key" style={{ color: '#718096', fontSize: 12 }}></i>
                                            <span>{attr.key.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')}</span>
                                        </label>
                                    </div>
                                    <div style={{ position: 'relative' }}>
                                        {attr.type === 'select' ? (
                                            <>
                                                <select
                                                    className={`form-select font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''}`}
                                                    value={(() => {
                                                        const storedValue = getAttributeValue(attr.key);
                                                        // Strip quotes if they exist (for backward compatibility)
                                                        if (typeof storedValue === 'string' && storedValue.startsWith('"') && storedValue.endsWith('"')) {
                                                            return storedValue.slice(1, -1);
                                                        }
                                                        return storedValue;
                                                    })()}
                                                    onChange={(e) => {
                                                        const selectedOption = (attr.options || dynamicSelectOptions[attr.key] || [])
                                                            .find(opt => opt.value === e.target.value);

                                                        handleAttributeChange(attr, selectedOption);
                                                    }}
                                                    onClick={() => handleSelectClick(attr)}
                                                    required={attr.required}
                                                    style={{
                                                        borderRadius: 8,
                                                        padding: '8px 12px',
                                                        // border: '1.5px solid #e1e5e9',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                        background: '#fff'
                                                    }}
                                                >
                                                    <option value="">Select {attr.key.split('_').map(word => word.toLowerCase()).join(' ')}</option>
                                                    {(attr.options || dynamicSelectOptions[attr.key] || []).map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) && (
                                                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                                        {String(node.data.errorMessage).toLowerCase().includes('deleted')
                                                            ? `${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} references a deleted connected app`
                                                            : `${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} is required`}
                                                    </div>
                                                )}
                                            </>
                                        ) : attr.type === 'password' || (attr.type !== 'select' && (attr.key.toLowerCase().includes('token') || attr.key.toLowerCase().includes('password'))) ? (
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type={showPassword[attr.key] ? "text" : "password"}
                                                    className={`form-control font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''}`}
                                                    value={stripQuotes(getAttributeValue(attr.key) || '')}
                                                    onChange={(e) => handleAttributeChange(attr, e.target.value)}
                                                    placeholder={`Enter ${attr.key.split('_').map(word => word.toLowerCase()).join(' ')}`}
                                                    required={attr.required}
                                                    style={{
                                                        borderRadius: 8,
                                                        padding: '8px 12px',
                                                        paddingRight: '40px',
                                                        // border: '1.5px solid #e1e5e9',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                        background: '#fff'
                                                    }}
                                                />
                                                <i
                                                    className={`fa-solid ${showPassword[attr.key] ? 'fa-eye' : 'fa-eye-slash'}`}
                                                    onClick={() => setShowPassword(prev => ({ ...prev, [attr.key]: !prev[attr.key] }))}
                                                    style={{
                                                        position: 'absolute',
                                                        right: 12,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        color: '#a0aec0',
                                                        cursor: 'pointer',
                                                        fontSize: 14,
                                                        transition: 'color 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.color = '#718096'}
                                                    onMouseLeave={(e) => e.target.style.color = '#a0aec0'}
                                                />
                                                {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) && (
                                                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                                        {`${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} is required`}
                                                    </div>
                                                )}
                                            </div>
                                        ) : attr.type === 'l_select' ? (
                                            <>
                                                <select
                                                    className={`form-select font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''}`}
                                                    value={(() => {
                                                        const storedValue = getAttributeValue(attr.key);
                                                        // Strip quotes if they exist (for backward compatibility)
                                                        if (typeof storedValue === 'string' && storedValue.startsWith('"') && storedValue.endsWith('"')) {
                                                            return storedValue.slice(1, -1);
                                                        }
                                                        return storedValue;
                                                    })()}
                                                    onChange={(e) => handleAttributeChange(attr, e.target.value)}
                                                    required={attr.required}
                                                    style={{
                                                        borderRadius: 8,
                                                        padding: '8px 12px',
                                                        // border: '1.5px solid #e1e5e9',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                        background: '#fff'
                                                    }}
                                                >
                                                    <option value="">Select LLM Configuration</option>
                                                    {uniqueLLMs.map((llm) => (
                                                        <option key={llm.data.displayName + llm.data.model}
                                                                value={llm.data.llmType !== 'litellm' ? llm.data.displayName : llm.data.model.split('/')[0]}>
                                                            {llm?.data?.llmType !== 'litellm' ? llm.data.displayName + ' - ' + llm.data.model : llm.data.model}
                                                        </option>
                                                    ))}
                                                </select>
                                                {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) && (
                                                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                                        {`${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} is required`}
                                                    </div>
                                                )}
                                            </>
                                        ) : attr.type === 'number' ? (
                                            <>
                                                <input
                                                    type="number"
                                                    className={`form-control font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''}`}
                                                    value={getAttributeValue(attr.key) || ''}
                                                    onChange={(e) => handleAttributeChange(attr, e.target.value)}
                                                    placeholder={`Enter ${attr.key.split('_').map(word => word.toLowerCase()).join(' ')}`}
                                                    required={attr.required}
                                                    style={{
                                                        borderRadius: 8,
                                                        padding: '8px 12px',
                                                        // border: '1.5px solid #e1e5e9',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                        background: '#fff'
                                                    }}
                                                />
                                                {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) && (
                                                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                                        {`${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} is required`}
                                                    </div>
                                                )}
                                            </>
                                        ) : attr.type === 'boolean' ? (
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={getAttributeValue(attr.key) || false}
                                                    onChange={(e) => handleAttributeChange(attr, e.target.checked)}
                                                    id={`check-${attr.key}`}
                                                    style={{
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <label
                                                    className="form-check-label font13"
                                                    htmlFor={`check-${attr.key}`}
                                                    style={{
                                                        cursor: 'pointer',
                                                        color: '#4a5568'
                                                    }}
                                                >
                                                    {attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                                                </label>
                                            </div>
                                        ) : attr.type === 'json' ? (
                                            <>
                        <textarea
                            className={`form-control font13${
                                (node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase())) ||
                                jsonFieldErrors?.[attr.key]
                                    ? ' is-invalid'
                                    : ''
                            }`}
                            value={getAttributeValue(attr.key) || ''}
                            onChange={(e) => {
                                const next = e.target.value;
                                let err = '';
                                if (next && next.trim().length > 0) {
                                    try {
                                        JSON.parse(next);
                                    } catch (parseErr) {
                                        err = 'Invalid JSON. Please enter valid JSON.';
                                    }
                                }
                                setJsonFieldErrors(prev => {
                                    if (!err && !prev?.[attr.key]) return prev;
                                    return { ...(prev || {}), [attr.key]: err };
                                });
                                handleAttributeChange(attr, next);
                            }}
                            onBlur={(e) => {
                                const current = e.target.value;
                                if (!current || current.trim().length === 0) return;
                                try {
                                    const formatted = JSON.stringify(JSON.parse(current), null, 2);
                                    if (formatted !== current) {
                                        handleAttributeChange(attr, formatted);
                                    }
                                    setJsonFieldErrors(prev => {
                                        if (!prev?.[attr.key]) return prev;
                                        return { ...(prev || {}), [attr.key]: '' };
                                    });
                                } catch {
                                }
                            }}
                            placeholder={`Enter ${attr.key.split('_').map(word => word.toLowerCase()).join(' ')} (JSON)`}
                            required={attr.required}
                            rows={6}
                            spellCheck={false}
                            style={{
                                borderRadius: 8,
                                padding: '8px 12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                background: '#fff',
                                resize: 'vertical',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                            }}
                        />
                                                {(jsonFieldErrors?.[attr.key] ||
                                                    (node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()))) && (
                                                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                                        {jsonFieldErrors?.[attr.key]
                                                            ? jsonFieldErrors[attr.key]
                                                            : `${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} is required`}
                                                    </div>
                                                )}
                                            </>
                                        ) : attr.type === 'textarea' ? (
                                            <>
                        <textarea
                            className={`form-control font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''}`}
                            value={getAttributeValue(attr.key) || ''}
                            onChange={(e) => handleAttributeChange(attr, e.target.value)}
                            placeholder={`Enter ${attr.key.split('_').map(word => word.toLowerCase()).join(' ')}`}
                            required={attr.required}
                            rows={4}
                            style={{
                                borderRadius: 8,
                                padding: '8px 12px',
                                // border: '1.5px solid #e1e5e9',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                background: '#fff',
                                resize: 'vertical'
                            }}
                        />
                                                {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) && (
                                                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                                        {`${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} is required`}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {attr.key === 'Mcp Environment' ? attr.value.map((item, index) => (
                                                    <div key={index} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 10 , paddingLeft: 20}}>
                                                        <label className={`font13 ${item.required ? 'required' : ''} mb-1 font-weight-bold`}>
                                                            {item.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                                                        </label>
                                                        {item?.type === 'select' ? (
                                                            <select
                                                                className={`form-select font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''}`}
                                                                value={(() => {
                                                                    const storedValue = node.data.attributes?.[item.key];
                                                                    if (storedValue === null || storedValue === undefined) {
                                                                        return Array.isArray(item.value) ? item.value.join(', ') : (item.value ?? '');
                                                                    }
                                                                    // If it's an object with value property, extract the value
                                                                    if (typeof storedValue === 'object' && storedValue !== null && 'value' in storedValue) {
                                                                        const value = storedValue.value;
                                                                        // Handle boolean false and 0 explicitly (they are falsy but valid values)
                                                                        if (value === false || value === 0) return value;
                                                                        return value || '';
                                                                    }
                                                                    // Strip quotes if they exist (for backward compatibility)
                                                                    if (typeof storedValue === 'string' && storedValue.startsWith('"') && storedValue.endsWith('"')) {
                                                                        return storedValue.slice(1, -1);
                                                                    }
                                                                    return storedValue;
                                                                })()}
                                                                onChange={(e) => handleAttributeChangeMCP(item, e.target.value)}
                                                                required={attr.required}
                                                                style={{
                                                                    borderRadius: 8,
                                                                    padding: '8px 12px',
                                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                                    background: '#fff'
                                                                }}
                                                            >
                                                                <option value="">Select {item.key.split('_').map(word => word.toLowerCase()).join(' ')}</option>
                                                                {item.options?.map((option) => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <input
                                                                type={item?.type ? item?.type : 'text'}
                                                                className={`form-control font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''}`}
                                                                value={(() => {
                                                                    const storedValue = node.data.attributes?.[item.key];
                                                                    if (storedValue === null || storedValue === undefined) {
                                                                        return Array.isArray(item.value) ? item.value.join(', ') : (item.value ?? '');
                                                                    }
                                                                    // If it's an object with value property, extract the value
                                                                    if (typeof storedValue === 'object' && storedValue !== null && 'value' in storedValue) {
                                                                        const value = storedValue.value;
                                                                        // Handle boolean false and 0 explicitly (they are falsy but valid values)
                                                                        if (value === false || value === 0) return value;
                                                                        return value || '';
                                                                    }
                                                                    // Strip quotes if they exist (for backward compatibility)
                                                                    if (typeof storedValue === 'string' && storedValue.startsWith('"') && storedValue.endsWith('"')) {
                                                                        return storedValue.slice(1, -1);
                                                                    }
                                                                    return storedValue;
                                                                })()}
                                                                onChange={(e) => handleAttributeChangeMCP(item, e.target.value)}
                                                                placeholder={`Enter ${item.key.split('_').map(word => word.toLowerCase()).join(' ')}`}
                                                                required={attr.required}
                                                                style={{
                                                                    borderRadius: 8,
                                                                    padding: '8px 12px',
                                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                                    background: '#fff'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                )) : attr.type === 'select' ? (
                                                    <>
                                                        <select
                                                            className={`form-select font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''} ${attr?.is_editable === false ? ' force-disabled' : ''}`}
                                                            value={
                                                                getAttributeValue(attr.key) ||
                                                                (Array.isArray(attr.value) ? attr.value.join(', ') : (attr.value ?? ''))
                                                            }
                                                            onChange={(e) => handleAttributeChange(attr, e.target.value)}
                                                            required={attr.required}
                                                            style={{
                                                                borderRadius: 8,
                                                                padding: '8px 12px',
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                                background: '#fff'
                                                            }}
                                                        >
                                                            <option value="">Select {attr.key.split('_').map(word => word.toLowerCase()).join(' ')}</option>
                                                            {(attr.options || dynamicSelectOptions[attr.key] || []).map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) && (
                                                            <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                                                {`${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} is required`}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (<input
                                                    type="text"
                                                    className={`form-control font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) ? ' is-invalid' : ''} ${attr?.is_editable === false ? ' force-disabled' : ''}`}
                                                    value={
                                                        getAttributeValue(attr.key) ||
                                                        (Array.isArray(attr.value) ? attr.value.join(', ') : (attr.value ?? ''))
                                                    }
                                                    onChange={(e) => handleAttributeChange(attr, e.target.value)}
                                                    placeholder={`Enter ${attr.key.split('_').map(word => word.toLowerCase()).join(' ')}`}
                                                    required={attr.required}
                                                />)}
                                                {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes(attr.key.toLowerCase()) && (
                                                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                                        {`${attr.key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} is required`}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {(attr.type === 'password' || (attr.type !== 'select' && attr.type !== 'l_select' && (attr.key.toLowerCase().includes('token') || attr.key.toLowerCase().includes('password')))) && (
                                            <i
                                                className={`fa-solid ${showPassword[attr.key] ? 'fa-eye' : 'fa-eye-slash'}`}
                                                onClick={() => setShowPassword(prev => ({ ...prev, [attr.key]: !prev[attr.key] }))}
                                                style={{
                                                    position: 'absolute',
                                                    right: 12,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    color: '#a0aec0',
                                                    cursor: 'pointer',
                                                    fontSize: 14,
                                                    transition: 'color 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = '#718096'}
                                                onMouseLeave={(e) => e.target.style.color = '#a0aec0'}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Documentation Link */}
                    {selectedTool?.handlerName !== 'MCPHandler'  && (
                        <a
                            href={selectedTool.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '10px 16px',
                                background: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: 8,
                                color: selectedTool.iconColor || '#4880FF',
                                textDecoration: 'none',
                                fontSize: 13,
                                fontWeight: 500,
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#f1f3f5';
                                e.currentTarget.style.borderColor = '#dee2e6';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#f8f9fa';
                                e.currentTarget.style.borderColor = '#e9ecef';
                            }}
                        >
                            <i className="fa-solid fa-book"></i>
                            <span>View Documentation</span>
                            <i className="fa-solid fa-external-link-alt" style={{ marginLeft: 'auto', fontSize: 12 }}></i>
                        </a>
                    )}
                </div>
            </div>
        );
    }

    if (node.type === 'agent') {
        // Default agent name logic
        let agentNumber = 1;
        if (node.id && node.id.startsWith('agent-')) {
            const parts = node.id.split('-');
            if (parts.length > 1 && !isNaN(Number(parts[1]))) {
                agentNumber = Number(parts[1]);
            }
        }
        const agentName = node.data.title || `Agent #${agentNumber}`;
        const connectedConfig = node.data.connectedConfig || { tool: [], llm: false, memory: false };
        let connectedLLMNode = null;
        if (connectedConfig.llm) {
            connectedLLMNode = nodes.find(n => n.type === 'config' && n.data.configType === 'llm' &&
                nodes.some(edgeNode => edgeNode.id === n.id)
            );
            if (!connectedLLMNode && llmNodes && llmNodes.length === 1) {
                connectedLLMNode = nodes.find(n => n.type === 'config' && n.data.configType === 'llm');
            }
        }
        const connectedToolNodes = (connectedConfig.tool || []).map(handlerName => {
            return toolNodes.find(t => t.handlerName === handlerName);
        }).filter(Boolean);

        const handleExpandClick = (type, value) => {
            setModalType(type);
            setModalValue(value);
            setShowPromptModal(true);
        };

        const handleModalSave = () => {
            if (modalType === 'systemprompt') {
                onChange({ ...node.data, systemPrompt: modalValue });
            } else if (modalType === 'role') {
                onChange({ ...node.data, role: modalValue });
            } else if (modalType === 'goal') {
                onChange({ ...node.data, goal: modalValue });
            }
            setShowPromptModal(false);
        };

        return (
            <div style={{  margin: 12, minWidth: 320 }}>
                <div style={{ padding: 5 }}>
                    <div style={{ marginBottom: 18, background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 10, padding: 12 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 6 }}>Connected LLM</div>
                        {connectedLLMNode ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ color: '#1976d2', fontSize: 16 }}>
                  <i className="fa-solid fa-brain"></i>
                </span>
                                <span style={{ fontWeight: 500 }}>{connectedLLMNode.data.llmType !== 'litellm' ? connectedLLMNode.data.llmType : connectedLLMNode.data.model.split('/')[0]}</span>
                                {connectedLLMNode.data.model && (
                                    <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>Model: {connectedLLMNode.data.model}</span>
                                )}
                            </div>
                        ) : (
                            <div style={{ color: '#aaa', fontSize: 13 }}>No LLM connected</div>
                        )}
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', margin: '10px 0 6px' }}>Connected Tools</div>
                        {connectedToolNodes.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                {connectedToolNodes.map(tool => (
                                    <div key={tool.handlerName} style={{ display: 'flex', alignItems: 'center', gap: 6}}>
                    <span style={{ color: tool.iconColor, fontSize: 16 }}>
                          {tool.icon?.startsWith('fa') && !tool.icon.includes('fa-circle') ? (
                              <span className="palette-node-icon" style={{ color: tool.iconColor }}><i className={tool.icon}></i></span>
                          ) : (
                              <span className='text-ellipsis' style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#fff',
                                  backgroundColor: tool.iconColor || '#6c757d',
                                  fontWeight: 700,
                                  textAlign: 'center',
                                  lineHeight: '18px',
                                  fontSize: 14,
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                              }}>
                              {tool.displayName?.[0]?.toUpperCase() || '?'}
                            </span>
                          )}
                        </span>
                                        <span style={{ fontSize: 13 }}>{tool.displayName}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ color: '#aaa', fontSize: 13 }}>No tools connected</div>
                        )}
                    </div>
                    <div style={{ marginBottom: 18 }}>
                        <label className="font14 required" style={{ color: '#000', display: 'flex', alignItems: 'center', gap: 8}}>
                            <FaUser className='font13' style={{ color: 'rgb(160 89 227)' }} /> <b>Agent Name</b>
                        </label>
                        <input
                            className={`form-control font13 ${node.data.errorMessage === 'title' || node.data.errorMessage === 'duplicate_title' ? ' is-invalid' : ''}`}
                            value={node.data.title || ''}
                            placeholder={`Agent #${agentNumber}`}
                            onChange={e => {
                                const newTitle = e.target.value;
                                const hasDuplicate = isDuplicateAgentTitle(newTitle, nodes, node.id);
                                onChange({
                                    ...node.data,
                                    title: newTitle,
                                    errorMessage: hasDuplicate ? 'duplicate_title' : ''
                                });
                            }}
                            style={{ borderRadius: 8, marginTop: 4 }}
                        />
                        {node.data.errorMessage === 'title' && (
                            <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                Agent name is missing or needs to be changed.
                            </div>
                        )}
                        {node.data.errorMessage === 'duplicate_title' && (
                            <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                Agent name is already used by another agent. Please choose a unique name.
                            </div>
                        )}
                    </div>

                    {/* Show Prompts Checkbox - custom styled */}
                    {/* <div className="custom-checkbox-card"> */}
                    <div style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: 'linear-gradient(90deg, rgba(160, 89, 227, 0.06) 0%, rgba(160, 89, 227, 0.02) 100%)',
                        borderLeft: '3px solid rgba(160, 89, 227, 0.3)',
                        marginBottom: 8
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 11,
                            color: '#5b4d6b',
                            marginBottom: 10
                        }}>
                            <FaInfoCircle style={{ flexShrink: 0, opacity: 0.8 }} />
                            <span>Add system instructions so this agent knows how to behave and what to do.</span>
                        </div>
                        <label className="custom-checkbox-label" style={{ marginBottom: 0 }}>
                            <input
                                type="checkbox"
                                checked={!!node.data.showPrompts}
                                onChange={e => onChange({ ...node.data, showPrompts: e.target.checked })}
                                className="custom-checkbox-input"
                            />
                            <span className="custom-checkbox-box"></span>
                            <span>
                  <span className="custom-checkbox-title">Prompts</span>
                </span>
                        </label>
                    </div>
                    <br />

                    {/* </div> */}

                    {/* Conditionally render prompts */}
                    {node.data.showPrompts && (
                        <>
                            <div style={{ marginBottom: 18 }}>
                                <label className="font14" style={{
                                    color: '#000',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}>
                                    <FaChalkboard className='font13' style={{ color: 'rgb(160 89 227)' }} />
                                    <b>System Prompt</b>
                                    <i
                                        className="fa fa-expand"
                                        style={{
                                            color: 'rgb(160 89 227)',
                                            fontSize: 12,
                                            cursor: 'pointer'
                                        }}
                                        title="Expand"
                                        onClick={() => handleExpandClick('systemprompt', node.data.systemPrompt || '')}
                                    />
                                </label>
                                <textarea
                                    className="form-control font13"
                                    value={node.data.systemPrompt || ''}
                                    onChange={e => onChange({ ...node.data, systemPrompt: e.target.value })}
                                    placeholder="System prompt for the agent"
                                    style={{ borderRadius: 8, marginTop: 4, minHeight: 60 }}
                                />
                            </div>
                        </>
                    )}

                    {/* Intelligence Decision Checkbox */}
                    {node.type === 'agent' && (isAgentInParallelGroup() || node.data.intelligenceDecisionFlag) && (
                        <>
                            <div style={{
                                padding: '10px 12px',
                                borderRadius: 8,
                                background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.04) 100%)',
                                borderLeft: '3px solid rgba(59, 130, 246, 0.45)',
                                marginBottom: 18
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 11,
                                    color: '#1e40af',
                                    marginBottom: 10
                                }}>
                                    <FaInfoCircle style={{ flexShrink: 0, opacity: 0.8 }} />
                                    <span>Use AI to choose which parallel agent runs based on the request—no manual routing.</span>
                                </div>
                                <label className="custom-checkbox-label" style={{ marginBottom: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={!!node.data.intelligenceDecisionFlag}
                                        onChange={e =>
                                            onChange({
                                                ...node.data,
                                                intelligenceDecisionFlag: e.target.checked,
                                                ...(e.target.checked
                                                    ? {}
                                                    : {
                                                        capabilities: undefined,
                                                        // store routing selection as a single value
                                                        intelligenceDecisionRoutingFlag: undefined,
                                                        // legacy fields (kept for backward compatibility)
                                                        AiRoutingFlag: undefined,
                                                        capabilitiesRoutingFlag: undefined
                                                    })
                                            })
                                        }
                                        className="custom-checkbox-input"
                                    />
                                    <span className="custom-checkbox-box"></span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="custom-checkbox-title">Intelligence Decision</span>
                  </span>
                                </label>

                                {/* Routing flags shown only when Intelligence Decision is enabled */}
                                {!!node.data.intelligenceDecisionFlag && (
                                    <div style={{
                                        marginTop: 10,
                                        paddingTop: 10,
                                        borderTop: '1px dashed rgba(59, 130, 246, 0.28)'
                                    }}>
                                        {(() => {
                                            const routingValue =
                                                node.data.intelligenceDecisionRoutingFlag ||
                                                (node.data.AiRoutingFlag ? 'AI' : (node.data.capabilitiesRoutingFlag ? 'capabilities' : ''));
                                            const name = `intelligence-decision-routing-${node.id}`;
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: '20px', marginLeft: '10px'}}>
                                                    <label className="custom-checkbox-label" style={{ marginBottom: 0 }}>
                                                        <input
                                                            type="radio"
                                                            name={name}
                                                            checked={routingValue === 'AI'}
                                                            onChange={() => onChange({
                                                                ...node.data,
                                                                intelligenceDecisionRoutingFlag: 'AI',
                                                                AiRoutingFlag: true,
                                                                capabilitiesRoutingFlag: false
                                                            })}
                                                            className="custom-checkbox-input"
                                                        />
                                                        <span className="custom-checkbox-box"></span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span className="custom-checkbox-title">AI</span>
                            </span>
                                                    </label>

                                                    <label className="custom-checkbox-label" style={{ marginBottom: 0 }}>
                                                        <input
                                                            type="radio"
                                                            name={name}
                                                            checked={routingValue === 'capabilities'}
                                                            onChange={() => onChange({
                                                                ...node.data,
                                                                intelligenceDecisionRoutingFlag: 'capabilities',
                                                                AiRoutingFlag: false,
                                                                capabilitiesRoutingFlag: true
                                                            })}
                                                            className="custom-checkbox-input"
                                                        />
                                                        <span className="custom-checkbox-box"></span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span className="custom-checkbox-title">Capabilities</span>
                            </span>
                                                    </label>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    <br />

                    <div style={{ marginBottom: 18 }}>
                        <label className="font14 required" style={{ color: '#000', display: 'flex', alignItems: 'center', gap: 8,}}>
                            <FaUserTie className='font13' style={{ color: 'rgb(160 89 227)' }} /> <b>Role</b>
                            <i
                                className="fa fa-expand"
                                style={{
                                    color: 'rgb(160 89 227)',
                                    fontSize: 12,
                                    cursor: 'pointer'
                                }}
                                title="Expand"
                                onClick={() => handleExpandClick('role', node.data.role || '')}
                            />
                        </label>
                        <textarea className={`form-control font13${node.data.errorMessage === 'role' ? ' is-invalid' : ''}`} value={node.data.role || ''} onChange={e => onChange({ ...node.data, role: e.target.value })} style={{ borderRadius: 8, marginTop: 4, minHeight: 40 }} />
                        {node.data.errorMessage === 'role' && (
                            <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                Role is required
                            </div>
                        )}
                    </div>
                    <div style={{ marginBottom: 18 }}>
                        <label className="font14 required" style={{ color: '#000', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FaRegThumbsUp className='font13' style={{ color: 'rgb(160 89 227)' }} /> <b>Goal</b>
                            <i
                                className="fa fa-expand"
                                style={{
                                    color: 'rgb(160 89 227)',
                                    fontSize: 12,
                                    cursor: 'pointer'
                                }}
                                title="Expand"
                                onClick={() => handleExpandClick('goal', node.data.goal || '')}
                            />
                        </label>
                        <textarea className={`form-control font13${node.data.errorMessage === 'goal' ? ' is-invalid' : ''}`} value={node.data.goal || ''} onChange={e => onChange({ ...node.data, goal: e.target.value })} style={{ borderRadius: 8, marginTop: 4, minHeight: 40 }} />
                        {node.data.errorMessage === 'goal' && (
                            <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                Goal is required
                            </div>
                        )}
                    </div>

                    {/* Agent tags: separate section under Goal (for agent nodes only) */}
                    {node.type === 'agent' && (
                        <div style={{ marginBottom: 18 }}>
                            <div style={{
                                borderRadius: 12,
                                background: 'transparent',
                                border: 'none',
                                boxShadow: 'none'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 6
                                }}>
                                    <FaTag style={{ color: 'rgb(160 89 227)', fontSize: 10 }} />
                                    <div>
                                        <div className="font14" style={{ fontWeight: 700, color: '#2d3748' }}>Tags</div>
                                    </div>
                                </div>
                                <TagInputComponent
                                    value={node.data.capabilities?.[node.id] || ''}
                                    variant="comfortable"
                                    placeholder="Type a tag and press Enter"
                                    onChange={(value) => onChange({
                                        ...node.data,
                                        capabilities: {
                                            ...(node.data.capabilities || {}),
                                            [node.id]: value
                                        }
                                    })}
                                />
                            </div>
                        </div>
                    )}
                    <div style={{ marginBottom: 0 }}>
                        <label className="font14" style={{ color: '#000', display: 'flex', alignItems: 'center', gap: 8,}}>
                            <FaRedo className='font13' style={{ color: 'rgb(160 89 227)' }} /> <b>Retry</b>
                        </label>
                        <input type="number" className="form-control font13" value={node.data.maxRetry || 1} onChange={e => onChange({ ...node.data, maxRetry: Number(e.target.value) })} style={{ borderRadius: 8, marginTop: 4, width: 120 }} />
                    </div>
                    {/* Advanced Settings Toggle */}
                    <button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: '#fff',
                            border: '1px solid #e9ecef',
                            borderRadius: 16,
                            color: '#2d3748',
                            fontSize: 13,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            marginBottom: showAdvanced ? 16 : 0,
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            marginTop: 15
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <i className="fa-solid fa-sliders" style={{ color: '#4880FF' }}></i>
                            Advanced Settings
                            {!!node.data.humanApproval && (
                                <span style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: '#15803d',
                                    background: '#dcfce7',
                                    borderRadius: 999,
                                    padding: '2px 7px',
                                }}>
                  Approval
                </span>
                            )}
                            {Array.isArray(node.data.policies) && node.data.policies.length > 0 && (
                                <span style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: '#1d4ed8',
                                    background: '#dbeafe',
                                    borderRadius: 999,
                                    padding: '2px 7px',
                                }}>
                  {node.data.policies.length} {node.data.policies.length === 1 ? 'policy' : 'policies'}
                </span>
                            )}
                        </div>
                        <i
                            className={`fa-solid fa-chevron-${showAdvanced ? 'up' : 'down'}`}
                            style={{
                                color: '#4880FF',
                                transition: 'transform 0.2s ease'
                            }}
                        />
                    </button>

                    {/* Advanced Settings Content */}
                    <div style={{
                        maxHeight: showAdvanced ? 'none' : '0',
                        overflow: showAdvanced ? 'visible' : 'hidden',
                        transition: 'opacity 0.2s ease-in-out',
                        opacity: showAdvanced ? 1 : 0
                    }}>
                        <div style={{
                            background: '#fff',
                            border: '1px solid #e9ecef',
                            borderRadius: 16,
                            padding: '10px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>

                            <label className="font13" style={{
                                color: '#4a5568',
                                marginBottom: 8,
                                display: 'block'
                            }}>
                                Output Type
                            </label>
                            <select
                                className={`form-select font13`}
                                value={node.data.outputType || 'json'}
                                onChange={(e) => onChange({ ...node.data, outputType: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 8,
                                    fontSize: 13,
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                }}
                            >
                                <option value="">Select Output Type</option>
                                {outputTypes?.map(output => (
                                    <option key={output.value} value={output.value}>{output.displayName}</option>
                                ))}
                            </select>

                            <label className="font13" style={{
                                color: '#4a5568',
                                marginBottom: 8,
                                marginTop: 10,
                                display: 'block'
                            }}>
                                Description
                            </label>

                            <input
                                className="form-control font13"
                                value={node.data.subtitle || ''}
                                placeholder={`Description`}
                                onChange={e => onChange({ ...node.data, subtitle: e.target.value })}
                                style={{ borderRadius: 8, marginTop: 4 }}
                            />

                            {showAdvanced ? <AgentAdvancedControls node={node} onChange={onChange} /> : null}

                            {/* Stop Workflow Switch */}
                            {/* <div style={{
                marginBottom: 16,
                marginTop: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                display: 'none',
              }}>
                <label
                  className="font14"
                  htmlFor="stopWorkflowSwitch"
                  style={{
                    cursor: 'pointer',
                    color: '#2c3e50',
                    marginBottom: 0,
                    flex: 1,
                    lineHeight: '1.5'
                  }}
                >
                 <b>Stop Workflow when goal is not satisfied</b>
                </label>
                <div className="form-check form-switch" style={{ marginBottom: 0, flexShrink: 0 ,marginTop: 6}}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    id="stopWorkflowSwitch"
                    checked={node.data.stopWorkflow || false}
                    onChange={(e) => onChange({ ...node.data, stopWorkflow: e.target.checked })}
                    style={{
                      cursor: 'pointer',
                      marginTop: 0
                    }}
                  />
                </div>
              </div> */}
                        </div>
                    </div>

                    {/* Prompt Modal Dialog for user and system prompts */}
                    {showPromptModal && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000
                        }}>
                            <div style={{
                                background: '#fff',
                                borderRadius: 16,
                                width: '80%',
                                maxWidth: '800px',
                                maxHeight: '80vh',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '24px 24px 20px 24px',
                                    borderBottom: '1px solid #e9ecef',
                                    background: '#fff',
                                    flexShrink: 0
                                }}>
                                    <h3 style={{
                                        margin: 0,
                                        color: '#2d3748',
                                        fontSize: '18px',
                                        fontWeight: 600
                                    }}>
                                        {modalType === 'role' ? 'Role' : modalType === 'goal' ? 'Goal' : 'System Prompt'}
                                    </h3>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        {modalValue && modalValue.trim() && (
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(modalValue);
                                                    const button = document.querySelector('.copy-btn');
                                                    if (button) {
                                                        const originalText = button.innerHTML;
                                                        button.innerHTML = '<i class="fa-solid fa-check"></i>';
                                                        button.style.borderColor = '#10b981';
                                                        button.style.color = '#10b981';
                                                        setTimeout(() => {
                                                            button.innerHTML = originalText;
                                                            button.style.background = '#f8fafc';
                                                            button.style.borderColor = '#e2e8f0';
                                                            button.style.color = '#64748b';
                                                        }, 2000);
                                                    }
                                                }}
                                                className="copy-btn"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '36px',
                                                    height: '36px',
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    color: '#64748b',
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.target.style.background = '#f1f5f9';
                                                    e.target.style.borderColor = '#cbd5e0';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.target.style.background = '#f8fafc';
                                                    e.target.style.borderColor = '#e2e8f0';
                                                }}
                                                title="Copy to clipboard"
                                            >
                                                <i className="fa-solid fa-copy"></i>
                                            </button>
                                        )}

                                        <button
                                            onClick={() => setShowPromptModal(false)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '20px',
                                                color: '#000',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                borderRadius: '4px',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseOver={(e) => e.target.style.color = '#718096'}
                                            onMouseOut={(e) => e.target.style.color = '#a0aec0'}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                                <div style={{
                                    flex: 1,
                                    padding: '24px',
                                    overflowY: 'auto',
                                    minHeight: 0
                                }}>
                  <textarea
                      value={modalValue}
                      onChange={(e) => setModalValue(e.target.value)}
                      placeholder={modalType === 'role' ? 'Enter role...' : modalType === 'goal' ? 'Enter goal...' : 'Enter system prompt...'}
                      style={{
                          width: '100%',
                          height: '250px',
                          padding: '16px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          overflowY: 'auto',
                          overflowX: 'auto',
                          scrollbarWidth: 'thin',
                          scrollbarColor: '#cbd5e0 #f1f5f9'
                      }}
                  />
                                </div>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '12px',
                                    padding: '20px 24px',
                                    borderTop: '1px solid #e9ecef',
                                    background: '#fff',
                                    flexShrink: 0
                                }}>
                                    <button
                                        onClick={() => setShowPromptModal(false)}
                                        style={{
                                            padding: '10px 20px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            background: '#fff',
                                            color: '#4a5568',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.borderColor = '#cbd5e0';
                                            e.target.style.backgroundColor = '#f7fafc';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.backgroundColor = '#fff';
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleModalSave}
                                        style={{
                                            padding: '10px 20px',
                                            border: 'none',
                                            borderRadius: '8px',
                                            background: 'rgb(160 89 227)',
                                            color: '#fff',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => e.target.style.background = 'rgb(140 69 207)'}
                                        onMouseOut={(e) => e.target.style.background = 'rgb(160 89 227)'}
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        );
    }

    if (node.type === 'task') {
        // Default agent name logic
        let agentNumber = 1;
        if (node.id && node.id.startsWith('agent-')) {
            const parts = node.id.split('-');
            if (parts.length > 1 && !isNaN(Number(parts[1]))) {
                agentNumber = Number(parts[1]);
            }
        }

        // Available methods list - get from node.data or use empty array
        const availableMethods = node.data?.availableMethods || [];

        // Method descriptions mapping - get from node.data or use empty object
        const methodDescriptions = node.data?.methodDescriptions || {};

        // Get tool icon and color from connected tool
        const getToolIcon = () => {
            const connectedTools = node.data?.connectedConfig?.tool || [];
            if (!connectedTools || connectedTools.length === 0) {
                return { icon: 'fa-solid fa-code', iconColor: 'rgb(160 89 227)', isSvg: false };
            }

            // Get first connected tool handlerName
            const firstToolHandler = Array.isArray(connectedTools) ? connectedTools[0] : connectedTools;
            if (!firstToolHandler) {
                return { icon: 'fa-solid fa-code', iconColor: 'rgb(160 89 227)', isSvg: false };
            }

            // Try to find tool node in nodes array
            const toolNode = nodes?.find(n =>
                n.type === 'config' &&
                n.data?.configType === 'tool' &&
                (n.data?.handlerName === firstToolHandler || n.data?.key === firstToolHandler)
            );

            // Check MCP tools first
            if (toolNode?.data?.key) {
                const mcpTool = mcpToolNodes.find(t => t.key === toolNode.data.key);
                if (mcpTool) {
                    return {
                        icon: mcpTool.icon || 'fa-solid fa-code',
                        iconColor: mcpTool.iconColor || mcpTool.color || 'rgb(160 89 227)',
                        isSvg: mcpTool.icon && (mcpTool.icon.startsWith('<svg') || mcpTool.icon.startsWith('<?xml'))
                    };
                }
            }

            // Check tool node data directly
            if (toolNode?.data?.icon) {
                return {
                    icon: toolNode.data.icon,
                    iconColor: toolNode.data.iconColor || toolNode.data.color || 'rgb(160 89 227)',
                    isSvg: toolNode.data.icon.startsWith('<svg') || toolNode.data.icon.startsWith('<?xml')
                };
            }

            // Check regular tools by handlerName
            if (toolNode?.data?.handlerName || firstToolHandler) {
                const handlerName = toolNode?.data?.handlerName || firstToolHandler;
                const toolConfig = toolNodes.find(t => t.handlerName === handlerName);
                if (toolConfig) {
                    return {
                        icon: toolConfig.icon || 'fa-solid fa-code',
                        iconColor: toolConfig.iconColor || toolConfig.color || 'rgb(160 89 227)',
                        isSvg: toolConfig.icon && (toolConfig.icon.startsWith('<svg') || toolConfig.icon.startsWith('<?xml'))
                    };
                }
            }

            return { icon: 'fa-solid fa-code', iconColor: 'rgb(160 89 227)', isSvg: false };
        };

        const toolIconInfo = getToolIcon();

        // Toggle method expansion
        const toggleMethodExpansion = (methodName) => {
            setExpandedMethods(prev => {
                const newSet = new Set(prev);
                if (newSet.has(methodName)) {
                    newSet.delete(methodName);
                } else {
                    newSet.add(methodName);
                }
                return newSet;
            });
        };

        // Use the buildInstructionStructure function from top level
        const buildInstructionStructureLocal = () => buildInstructionStructure(instructionGroups);

        // Add a new group (parallel or sequence)
        const addGroup = (type) => {
            const newGroup = {
                id: Date.now().toString(),
                type: type, // 'parallel' or 'sequence'
                methods: []
            };
            setInstructionGroups([...instructionGroups, newGroup]);
        };

        // Remove a group
        const removeGroup = (groupId) => {
            setInstructionGroups(instructionGroups.filter(g => g.id !== groupId));
        };

        // Add method to a group
        const addMethodToGroup = (groupId, methodName) => {
            setInstructionGroups(instructionGroups.map(group => {
                if (group.id === groupId) {
                    // Check if method already exists in this group
                    const methodExists = group.methods.some(m => m.name === methodName);
                    if (methodExists) {
                        // Don't add duplicate method
                        return group;
                    }

                    // Get method description to auto-add parameters
                    const methodInfo = methodDescriptions[methodName] || {};
                    const availableParams = methodInfo.parameters || [];

                    // Initialize params object with all available parameters
                    const initialParams = {};
                    availableParams.forEach(param => {
                        const paramName = typeof param === 'string' ? param : param.name;
                        // Initialize with empty string, will be filled by user
                        initialParams[paramName] = '';
                    });

                    // Use existing methodParams if available, otherwise use initialized params
                    const params = methodParams[methodName] || initialParams;

                    const newMethod = {
                        id: Date.now().toString() + Math.random(),
                        name: methodName,
                        params: params
                    };

                    // Update methodParams state to include the new method's params
                    setMethodParams(prev => ({
                        ...prev,
                        [methodName]: params
                    }));

                    return {
                        ...group,
                        methods: [...group.methods, newMethod]
                    };
                }
                return group;
            }));
        };

        // Remove method from a group
        const removeMethodFromGroup = (groupId, methodId) => {
            setInstructionGroups(instructionGroups.map(group => {
                if (group.id === groupId) {
                    return {
                        ...group,
                        methods: group.methods.filter(m => m.id !== methodId)
                    };
                }
                return group;
            }));
        };

        // Update method parameters
        const updateMethodParams = (methodName, params) => {
            setMethodParams({
                ...methodParams,
                [methodName]: params
            });

            // Also update params in all groups
            setInstructionGroups(instructionGroups.map(group => ({
                ...group,
                methods: group.methods.map(m =>
                    m.name === methodName ? { ...m, params: params } : m
                )
            })));
        };

        // Update parameter value for a specific method in a group
        const updateMethodParamValue = (groupId, methodId, key, value) => {
            setInstructionGroups(instructionGroups.map(group => {
                if (group.id === groupId) {
                    return {
                        ...group,
                        methods: group.methods.map(m => {
                            if (m.id === methodId) {
                                const newParams = { ...m.params, [key]: value };
                                return { ...m, params: newParams };
                            }
                            return m;
                        })
                    };
                }
                return group;
            }));
        };

        // Save the built instruction structure
        const saveInstructions = () => {
            const builtInstructions = buildInstructionStructureLocal();
            onChange({
                ...node.data,
                instructions: builtInstructions,
                instructionGroups: instructionGroups,
                methodParams: methodParams
            });
        };

        return (
            <div style={{ margin: 12, minWidth: 320, maxWidth: 500 }}>
                <div style={{ padding: 5 }}>
                    <div style={{ marginBottom: 18 }}>
                        <label className="font14 required" style={{ color: '#000', display: 'flex', alignItems: 'center', gap: 8}}>
                            <FaUser className='font13' style={{ color: 'rgb(160 89 227)' }} /> <b>Task Name</b>
                        </label>
                        <input
                            className={`form-control font13 ${node.data.errorMessage === 'title' || node.data.errorMessage === 'duplicate_title' ? ' is-invalid' : ''}`}
                            value={node.data.title || ''}
                            placeholder={`Task #${agentNumber}`}
                            onChange={e => {
                                const newTitle = e.target.value;
                                const hasDuplicate = isDuplicateAgentTitle(newTitle, nodes, node.id);
                                onChange({
                                    ...node.data,
                                    title: newTitle,
                                    errorMessage: hasDuplicate ? 'duplicate_title' : ''
                                });
                            }}
                            style={{ borderRadius: 8, marginTop: 4 }}
                        />
                        {node.data.errorMessage === 'title' && (
                            <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                Task name is missing or needs to be changed.
                            </div>
                        )}
                        {node.data.errorMessage === 'duplicate_title' && (
                            <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                                Task name is already used by another agent. Please choose a unique name.
                            </div>
                        )}
                    </div>

                    {/* Method Instructions Section */}
                    <div style={{
                        background: '#f8f9fa',
                        border: node.data.errorMessage === 'instructions' ? '1px solid #dc3545' : '1px solid #e9ecef',
                        borderRadius: 12,
                        padding: '16px',
                        marginBottom: 18
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 16
                        }}>
                            <label className="font14" style={{ color: '#000', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FaLayerGroup className='font13' style={{ color: 'rgb(160 89 227)' }} />
                                <b>Method Instructions</b>
                            </label>
                        </div>
                        {node.data.errorMessage === 'instructions' && (
                            <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 12 }}>
                                Instructions are required when a tool is connected. Please add instructions to the task agent.
                            </div>
                        )}

                        {/* Add Group Buttons */}
                        {availableMethods.length > 0 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 16
                            }}>
                                <label className="font13" style={{
                                    color: '#4a5568',
                                    fontWeight: 600
                                }}>
                                    Organize Methods
                                </label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        onClick={() => addGroup('parallel')}
                                        style={{
                                            padding: '6px 12px',
                                            background: '#3b82f6',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 6,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                        title="Add Parallel Group"
                                    >
                                        <FaPlus size={10} />
                                        Parallel
                                    </button>
                                    <button
                                        onClick={() => addGroup('sequence')}
                                        style={{
                                            padding: '6px 12px',
                                            background: '#10b981',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 6,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                        title="Add Sequence Group"
                                    >
                                        <FaPlus size={10} />
                                        Sequence
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Instruction Groups */}
                        {instructionGroups.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: '#6b7280',
                                fontSize: 13
                            }}>
                                No instruction groups yet. Add a parallel or sequence group to get started.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {instructionGroups.map((group, groupIndex) => (
                                    <div
                                        key={group.id}
                                        style={{
                                            background: '#fff',
                                            border: `2px solid ${group.type === 'parallel' ? '#3b82f6' : '#10b981'}`,
                                            borderRadius: 10,
                                            padding: '12px',
                                            position: 'relative'
                                        }}
                                    >
                                        {/* Group Header */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: 12,
                                            paddingBottom: 8,
                                            borderBottom: '1px solid #e5e7eb'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{
                                                    padding: '4px 10px',
                                                    background: group.type === 'parallel' ? '#dbeafe' : '#d1fae5',
                                                    color: group.type === 'parallel' ? '#1e40af' : '#065f46',
                                                    borderRadius: 6,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {group.type}
                                                </div>
                                                <span style={{ fontSize: 12, color: '#6b7280' }}>
                          {group.methods.length} method{group.methods.length !== 1 ? 's' : ''}
                        </span>
                                            </div>
                                            <button
                                                onClick={() => removeGroup(group.id)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#ef4444',
                                                    cursor: 'pointer',
                                                    padding: '4px 8px',
                                                    borderRadius: 4,
                                                    fontSize: 12
                                                }}
                                                title="Remove Group"
                                            >
                                                <FaTrash size={12} />
                                            </button>
                                        </div>

                                        {/* Methods in Group */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {group.methods.length === 0 ? (
                                                <div style={{
                                                    textAlign: 'center',
                                                    padding: '12px',
                                                    color: '#9ca3af',
                                                    fontSize: 12,
                                                    fontStyle: 'italic'
                                                }}>
                                                    No methods in this group
                                                </div>
                                            ) : (
                                                group.methods.map((method, methodIndex) => (
                                                    <div
                                                        key={method.id}
                                                        style={{
                                                            background: '#f9fafb',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: 8,
                                                            padding: '10px',
                                                            position: 'relative'
                                                        }}
                                                    >
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            marginBottom: 8
                                                        }}>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 8
                                                            }}>
                                                                <FaGripVertical style={{ color: '#9ca3af', fontSize: 12 }} />
                                                                <span style={{
                                                                    fontWeight: 600,
                                                                    fontSize: 13,
                                                                    color: '#1f2937'
                                                                }}>
                                  {method.name}
                                </span>
                                                            </div>
                                                            <button
                                                                onClick={() => removeMethodFromGroup(group.id, method.id)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: '#ef4444',
                                                                    cursor: 'pointer',
                                                                    padding: '2px 6px',
                                                                    fontSize: 11
                                                                }}
                                                                title="Remove Method"
                                                            >
                                                                <FaTrash size={10} />
                                                            </button>
                                                        </div>

                                                        {/* Method Parameters */}
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                                                            {Object.keys(method.params || {}).map(paramKey => {
                                                                // Get parameter info from method description
                                                                const methodInfo = methodDescriptions[method.name] || {};
                                                                const paramInfo = methodInfo.parameters?.find(p =>
                                                                    (typeof p === 'string' ? p : p.name) === paramKey
                                                                );
                                                                const paramType = typeof paramInfo === 'object' ? paramInfo.type : 'text';
                                                                const isOptional = typeof paramInfo === 'object' ? (paramInfo.isOptional || false) : false;

                                                                return (
                                                                    <div key={paramKey} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                        <div style={{
                                                                            flex: '0 0 120px',
                                                                            padding: '4px 8px',
                                                                            border: '1px solid #d1d5db',
                                                                            borderRadius: 4,
                                                                            fontSize: 11,
                                                                            background: '#f9fafb',
                                                                            color: '#374151',
                                                                            fontWeight: 500,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between'
                                                                        }}>
                                                                            <span>{paramKey}</span>
                                                                            {isOptional && (
                                                                                <span style={{ fontSize: 9, color: '#6b7280', marginLeft: 4 }}>(opt)</span>
                                                                            )}
                                                                        </div>
                                                                        <div style={{
                                                                            flex: 1,
                                                                            position: 'relative',
                                                                            minWidth: 0,
                                                                            maxWidth: '100%',
                                                                            overflow: 'hidden'
                                                                        }}>
                                                                            {paramType === 'json' ? (
                                                                                <>
                                          <textarea
                                              value={method.params[paramKey] || ''}
                                              onChange={(e) => {
                                                  const next = e.target.value;
                                                  const errorKey = `${group.id}:${method.id}:${paramKey}`;
                                                  let err = '';
                                                  if (next && next.trim().length > 0) {
                                                      try {
                                                          JSON.parse(next);
                                                      } catch (parseErr) {
                                                          err = 'Invalid JSON. Please enter valid JSON (object/array/string/number/boolean/null).';
                                                      }
                                                  }
                                                  setJsonFieldErrors(prev => {
                                                      if (!err && !prev?.[errorKey]) return prev;
                                                      return { ...(prev || {}), [errorKey]: err };
                                                  });
                                                  updateMethodParamValue(group.id, method.id, paramKey, next);
                                              }}
                                              onBlur={(e) => {
                                                  const current = e.target.value;
                                                  if (!current || current.trim().length === 0) return;
                                                  const errorKey = `${group.id}:${method.id}:${paramKey}`;
                                                  try {
                                                      const formatted = JSON.stringify(JSON.parse(current), null, 2);
                                                      if (formatted !== current) {
                                                          updateMethodParamValue(group.id, method.id, paramKey, formatted);
                                                      }
                                                      setJsonFieldErrors(prev => {
                                                          if (!prev?.[errorKey]) return prev;
                                                          return { ...(prev || {}), [errorKey]: '' };
                                                      });
                                                  } catch {
                                                  }
                                              }}
                                              placeholder="Value (json)"
                                              spellCheck={false}
                                              style={{
                                                  width: '100%',
                                                  padding: '4px 8px',
                                                  border: `1px solid ${
                                                      jsonFieldErrors?.[`${group.id}:${method.id}:${paramKey}`]
                                                          ? '#ef4444'
                                                          : '#d1d5db'
                                                  }`,
                                                  borderRadius: 4,
                                                  fontSize: 11,
                                                  minHeight: '70px',
                                                  resize: 'vertical',
                                                  fontFamily:
                                                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                  boxSizing: 'border-box'
                                              }}
                                          />
                                                                                    {jsonFieldErrors?.[`${group.id}:${method.id}:${paramKey}`] && (
                                                                                        <div style={{ color: '#ef4444', fontSize: 10, marginTop: 4 }}>
                                                                                            {jsonFieldErrors[`${group.id}:${method.id}:${paramKey}`]}
                                                                                        </div>
                                                                                    )}
                                                                                </>
                                                                            ) : paramType === 'textarea' ? (
                                                                                <textarea
                                                                                    value={method.params[paramKey] || ''}
                                                                                    onChange={(e) => updateMethodParamValue(group.id, method.id, paramKey, e.target.value)}
                                                                                    placeholder={`Value (${paramType})`}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        padding: '4px 8px',
                                                                                        border: '1px solid #d1d5db',
                                                                                        borderRadius: 4,
                                                                                        fontSize: 11,
                                                                                        minHeight: '60px',
                                                                                        resize: 'vertical',
                                                                                        fontFamily: 'inherit',
                                                                                        boxSizing: 'border-box'
                                                                                    }}
                                                                                />
                                                                            ) : paramType === 'file' ? (
                                                                                <FileUploadComponent
                                                                                    value={method.params[paramKey] || ''}
                                                                                    onChange={(value) => updateMethodParamValue(group.id, method.id, paramKey, value)}
                                                                                    maxFiles={5}
                                                                                />
                                                                            ) : paramType === 'tag' ? (
                                                                                <TagInputComponent
                                                                                    value={method.params[paramKey] || ''}
                                                                                    onChange={(value) => updateMethodParamValue(group.id, method.id, paramKey, value)}
                                                                                />
                                                                            ) : (
                                                                                <input
                                                                                    type={paramType === 'number' ? 'number' : 'text'}
                                                                                    value={method.params[paramKey] || ''}
                                                                                    onChange={(e) => updateMethodParamValue(group.id, method.id, paramKey, e.target.value)}
                                                                                    placeholder={`Value (${paramType})`}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        padding: '4px 8px',
                                                                                        border: '1px solid #d1d5db',
                                                                                        borderRadius: 4,
                                                                                        fontSize: 11,
                                                                                        boxSizing: 'border-box'
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <button
                                                                            onClick={() => {
                                                                                const newParams = { ...method.params };
                                                                                delete newParams[paramKey];
                                                                                setInstructionGroups(instructionGroups.map(g => {
                                                                                    if (g.id === group.id) {
                                                                                        return {
                                                                                            ...g,
                                                                                            methods: g.methods.map(m => {
                                                                                                if (m.id === method.id) {
                                                                                                    return { ...m, params: newParams };
                                                                                                }
                                                                                                return m;
                                                                                            })
                                                                                        };
                                                                                    }
                                                                                    return g;
                                                                                }));
                                                                            }}
                                                                            style={{
                                                                                background: '#ef4444',
                                                                                color: '#fff',
                                                                                border: 'none',
                                                                                borderRadius: 4,
                                                                                padding: '4px 8px',
                                                                                cursor: 'pointer',
                                                                                fontSize: 10
                                                                            }}
                                                                            title="Remove Parameter"
                                                                        >
                                                                            <FaTimes size={8} />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                            {(!method.params || Object.keys(method.params).length === 0) && (
                                                                <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginBottom: 4 }}>
                                                                    No parameters configured
                                                                </div>
                                                            )}
                                                            {/* Add Parameter Dropdown */}
                                                            {(() => {
                                                                const methodInfo = methodDescriptions[method.name] || {};
                                                                const availableParams = methodInfo.parameters || [];
                                                                const usedParamKeys = Object.keys(method.params || {});
                                                                const unusedParams = availableParams.filter(p => {
                                                                    const paramName = typeof p === 'string' ? p : p.name;
                                                                    return !usedParamKeys.includes(paramName);
                                                                });

                                                                if (unusedParams.length === 0 && availableParams.length > 0) {
                                                                    return (
                                                                        <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginTop: 4 }}>
                                                                            All parameters added
                                                                        </div>
                                                                    );
                                                                }

                                                                return (
                                                                    <select
                                                                        value=""
                                                                        onChange={(e) => {
                                                                            if (e.target.value) {
                                                                                const paramName = e.target.value;
                                                                                const newParams = { ...(method.params || {}), [paramName]: '' };
                                                                                setInstructionGroups(instructionGroups.map(g => {
                                                                                    if (g.id === group.id) {
                                                                                        return {
                                                                                            ...g,
                                                                                            methods: g.methods.map(m => {
                                                                                                if (m.id === method.id) {
                                                                                                    return { ...m, params: newParams };
                                                                                                }
                                                                                                return m;
                                                                                            })
                                                                                        };
                                                                                    }
                                                                                    return g;
                                                                                }));
                                                                                e.target.value = '';
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '6px 10px',
                                                                            border: '1px dashed #d1d5db',
                                                                            borderRadius: 6,
                                                                            fontSize: 11,
                                                                            background: '#f3f4f6',
                                                                            color: '#374151',
                                                                            cursor: 'pointer',
                                                                            marginTop: 4
                                                                        }}
                                                                        onMouseOver={(e) => {
                                                                            e.target.style.background = '#e5e7eb';
                                                                            e.target.style.borderColor = '#9ca3af';
                                                                        }}
                                                                        onMouseOut={(e) => {
                                                                            e.target.style.background = '#f3f4f6';
                                                                            e.target.style.borderColor = '#d1d5db';
                                                                        }}
                                                                    >
                                                                        <option value="">+ Add Parameter</option>
                                                                        {unusedParams.map((param, idx) => {
                                                                            const paramName = typeof param === 'string' ? param : param.name;
                                                                            const paramType = typeof param === 'object' ? param.type : 'text';
                                                                            const isOptional = typeof param === 'object' ? (param.isOptional || false) : false;
                                                                            return (
                                                                                <option key={idx} value={paramName}>
                                                                                    {paramName} ({paramType}){isOptional ? ' - optional' : ''}
                                                                                </option>
                                                                            );
                                                                        })}
                                                                    </select>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                ))
                                            )}

                                            {/* Add Method to Group */}
                                            <div style={{
                                                border: '2px dashed #d1d5db',
                                                borderRadius: 8,
                                                padding: '8px',
                                                background: '#fafafa'
                                            }}>
                                                <select
                                                    value=""
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            addMethodToGroup(group.id, e.target.value);
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px 8px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: 6,
                                                        fontSize: 12,
                                                        background: '#fff',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <option value="">+ Add Method</option>
                                                    {availableMethods
                                                        .filter(methodName => !group.methods.some(m => m.name === methodName))
                                                        .map(methodName => (
                                                            <option key={methodName} value={methodName}>
                                                                {methodName}
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Instruction Preview */}
                        {instructionGroups.length > 0 && (
                            <div style={{
                                marginTop: 16,
                                padding: '12px',
                                background: '#1f2937',
                                borderRadius: 8,
                                border: '1px solid #374151'
                            }}>
                                <div style={{
                                    color: '#9ca3af',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    marginBottom: 8,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Instruction Preview
                                </div>
                                <pre style={{
                                    color: '#10b981',
                                    fontSize: 11,
                                    margin: 0,
                                    padding: '8px',
                                    background: '#111827',
                                    borderRadius: 6,
                                    overflow: 'auto',
                                    maxHeight: '200px',
                                    fontFamily: 'Monaco, "Courier New", monospace',
                                    lineHeight: '1.5'
                                }}>
                  {JSON.stringify(buildInstructionStructureLocal(), null, 2)}
                </pre>
                            </div>
                        )}


                        {/* Available Methods List */}
                        <div style={{
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            padding: '12px',
                            marginTop: '16px'
                        }}>
                            <label className="font13" style={{
                                color: '#4a5568',
                                marginBottom: 12,
                                display: 'block',
                                fontWeight: 600
                            }}>
                                Available Methods
                            </label>
                            {availableMethods.length > 0 ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 5
                                }}>
                                    {availableMethods.map((methodName, index) => {
                                        const isExpanded = expandedMethods.has(methodName);
                                        const methodInfo = methodDescriptions[methodName] || {
                                            description: 'No description available for this method.',
                                            parameters: [],
                                            returnType: 'Unknown'
                                        };

                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    overflow: 'hidden',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    transition: 'all 0.2s ease',
                                                    background: '#fff',
                                                }}
                                            >
                                                {/* Method Header - Clickable */}
                                                <div
                                                    onClick={() => toggleMethodExpansion(methodName)}
                                                    style={{
                                                        padding: '7px 11px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        background: isExpanded ? '#f8f9fa' : '#fff',
                                                        transition: 'background 0.2s ease',
                                                        borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isExpanded) {
                                                            e.currentTarget.style.background = '#f8f9fa';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isExpanded) {
                                                            e.currentTarget.style.background = '#fff';
                                                        }
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 10,
                                                        flex: 1
                                                    }}>
                                                        <div style={{
                                                            width: 32,
                                                            height: 32,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}>
                                                            {toolIconInfo.isSvg ? (
                                                                <span
                                                                    className='tool-img-big'
                                                                    dangerouslySetInnerHTML={{ __html: toolIconInfo.icon }}
                                                                    style={{ width: 32, height: 32 }}
                                                                />
                                                            ) : toolIconInfo.icon?.startsWith('fa') ? (
                                                                <i className={toolIconInfo.icon} style={{ fontSize: 18, color: toolIconInfo.iconColor || '#6c757d' }}></i>
                                                            ) : (
                                                                <i className="fa-solid fa-code" style={{ fontSize: 18, color: '#6c757d' }}></i>
                                                            )}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{
                                                                fontSize: 13,
                                                                fontWeight: 600,
                                                                color: '#1f2937'
                                                            }}>
                                                                {methodName}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        marginLeft: 12
                                                    }}>
                                                        <i
                                                            className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`}
                                                            style={{
                                                                color: '#9ca3af',
                                                                fontSize: 11,
                                                                transition: 'transform 0.2s ease',
                                                                marginLeft: 4
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Expanded Content */}
                                                {isExpanded && (
                                                    <div style={{
                                                        padding: '14px',
                                                        background: '#fafafa',
                                                        borderTop: '1px solid #e5e7eb',
                                                        transition: 'all 0.2s ease'
                                                    }}>
                                                        {/* Description */}
                                                        <div style={{ marginBottom: 12 }}>
                                                            <div style={{
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                color: '#4b5563',
                                                                marginBottom: 6,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.5px'
                                                            }}>
                                                                Description
                                                            </div>
                                                            <div style={{
                                                                fontSize: 12,
                                                                color: '#374151',
                                                                lineHeight: '1.6'
                                                            }}>
                                                                {methodInfo.description}
                                                            </div>
                                                        </div>

                                                        {/* Parameters */}
                                                        {methodInfo.parameters && methodInfo.parameters.length > 0 && (
                                                            <div style={{ marginBottom: 12 }}>
                                                                <div style={{
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    color: '#4b5563',
                                                                    marginBottom: 6,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.5px'
                                                                }}>
                                                                    Parameters
                                                                </div>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 8
                                                                }}>
                                                                    {methodInfo.parameters.map((param, paramIndex) => {
                                                                        // Handle both string and object parameter formats
                                                                        const paramName = typeof param === 'string' ? param : param.name;
                                                                        const paramType = typeof param === 'object' ? param.type : 'text';
                                                                        const isOptional = typeof param === 'object' ? (param.isOptional || false) : false;

                                                                        return (
                                                                            <div
                                                                                key={paramIndex}
                                                                                style={{
                                                                                    padding: '8px 12px',
                                                                                    background: '#fff',
                                                                                    border: '1px solid #d1d5db',
                                                                                    borderRadius: 6,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'space-between'
                                                                                }}
                                                                            >
                                                                                <div style={{ flex: 1 }}>
                                                                                    <div style={{
                                                                                        fontSize: 12,
                                                                                        fontWeight: 600,
                                                                                        color: '#374151',
                                                                                        marginBottom: 2
                                                                                    }}>
                                                                                        {paramName}
                                                                                        {isOptional && (
                                                                                            <span style={{
                                                                                                fontSize: 10,
                                                                                                color: '#6b7280',
                                                                                                marginLeft: 6,
                                                                                                fontStyle: 'italic'
                                                                                            }}>
                                                (optional)
                                              </span>
                                                                                        )}
                                                                                    </div>
                                                                                    <div style={{
                                                                                        fontSize: 11,
                                                                                        color: '#6b7280'
                                                                                    }}>
                                                                                        Type: {paramType}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{
                                    color: '#9ca3af',
                                    fontSize: 12,
                                    fontStyle: 'italic',
                                    padding: '16px',
                                    textAlign: 'center',
                                    background: '#f8f9fa',
                                    borderRadius: 8,
                                    border: '1px dashed #e5e7eb'
                                }}>
                                    {node.data?.connectedConfig?.tool?.length > 0
                                        ? 'No methods found in connected tool. Please ensure the tool has methods available.'
                                        : 'Connect a tool to this task agent to see available methods.'}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Advanced Settings Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: 16,
              color: '#2d3748',
              fontSize: 13,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              marginBottom: showAdvanced ? 16 : 0,
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              marginTop: 15
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <i className="fa-solid fa-sliders" style={{ color: '#4880FF' }}></i>
              Advanced Settings
              {!!node.data.humanApproval && (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#15803d',
                  background: '#dcfce7',
                  borderRadius: 999,
                  padding: '2px 7px',
                }}>
                  Approval
                </span>
              )}
              {Array.isArray(node.data.policies) && node.data.policies.length > 0 && (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#1d4ed8',
                  background: '#dbeafe',
                  borderRadius: 999,
                  padding: '2px 7px',
                }}>
                  {node.data.policies.length} {node.data.policies.length === 1 ? 'policy' : 'policies'}
                </span>
              )}
            </div>
            <i
              className={`fa-solid fa-chevron-${showAdvanced ? 'up' : 'down'}`}
              style={{
                color: '#4880FF',
                transition: 'transform 0.2s ease'
              }}
            />
          </button>

          {/* Advanced Settings Content */}
          <div style={{
            maxHeight: showAdvanced ? 'none' : '0',
            overflow: showAdvanced ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease-in-out',
            opacity: showAdvanced ? 1 : 0
          }}>
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: 16,
              padding: '10px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              {showAdvanced ? <AgentAdvancedControls node={node} onChange={onChange} /> : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LLM Configuration
  if (node?.type === 'config' && node?.data?.configType === 'llm') {
    const selectedLLM = llmNodes.find(l => l.llmType === node.data.llmType);
    const isCustomLlm = isCustomLlmType(node.data.llmType);

    return (
      <div style={{ margin: 12, minWidth: 320 }}>
        <div style={{ padding: 5 }}>
          {/* Basic Settings Card */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: 16,
            padding: '10px',
            marginBottom: 16,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            {/* Selected LLM Type */}
            <div className="form-group mb-4">
              <div style={{
                background: '#f8f9fa',
                padding: '12px 16px',
                borderRadius: 12,
                marginBottom: 20
              }}>
                <label className="font13" style={{ 
                  color: '#4a5568',
                  marginBottom: 8,
                  display: 'block'
                }}>
                  Selected Type
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: '#2d3748',
                  fontSize: 14,
                  fontWeight: 600
                }}>
                {isCustomLlm ? (
                  <>
                    <span style={{ color: selectedLLM?.iconColor || '#5b6b7c' }}>
                      {selectedLLM?.icon || (
                        <img src={customLlmIcon} alt="Custom LLM" width={18} height={18} style={{ objectFit: 'contain' }} />
                      )}
                    </span>
                    <span>{selectedLLM?.displayName || node.data.displayName || 'Custom LLM'}</span>
                  </>
                ) : selectedLLM?.llmType !== 'litellm' ? (
                  <>
                    <span style={{ color: selectedLLM?.iconColor }}>{selectedLLM?.icon}</span>
                    <span>{selectedLLM?.displayName || node.data.llmType}</span>
                  </>
                ) : (
                  <span>{selectedLLM?.llmModels.find(model => model.name === node.data.model)?.name}</span>
                )}
                </div> 
              </div>

              {/* Display Name Input */}
              <div style={{ marginBottom: 20 }}>
                <label className="font13" style={{ 
                  color: '#4a5568',
                  marginBottom: 8,
                  display: 'block'
                }}>
                  Display Name
                </label>
                <input
                  type="text"
                  key={node.data.model}
                  className="form-control font13"
                  placeholder="Enter a display name for this LLM"
                  value={node.data.llmType !== 'litellm' ? node.data.displayName : node.data.model.split('/')[0]}
                  onChange={(e) => onChange({ ...node.data, displayName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                  }}
                />
              </div>

              {/* Model Selection */}
              {isCustomLlm ? (
                <div style={{ marginBottom: 20 }}>
                  <label className="font13 required" style={{ 
                    color: '#4a5568',
                    marginBottom: 8,
                    display: 'block'
                  }}>
                    Model name
                  </label>
                  <input
                    type="text"
                    className={`form-control font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes('model') ? ' is-invalid' : ''}`}
                    placeholder="Enter model name"
                    value={node.data.model || ''}
                    onChange={(e) => onChange({ ...node.data, model: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      fontSize: 13,
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                    }}
                  />
                  {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes('model') && (
                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                      {node.data.errorMessage}
                    </div>
                  )}
                </div>
              ) : selectedLLM && (
                <div style={{ marginBottom: 20 }}>
                  <label className="font13 required" style={{ 
                    color: '#4a5568',
                    marginBottom: 8,
                    display: 'block'
                  }}>
                    Model
                  </label>
                  <select
                    className={`form-select font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes('model') ? ' is-invalid' : ''}`}
                    value={node.data.model || ''}
                    onChange={(e) => onChange({ ...node.data, model: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      fontSize: 13,
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                    }}
                  >
                    <option value="">Select a model</option>
                    {selectedLLM.llmModels?.map(model => (
                      <option key={model.name} value={model.name}>{selectedLLM?.llmType !== 'litellm' ? model.displayName : model.name}</option>
                    ))}
                  </select>
                  {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes('model') && (
                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                      {node.data.errorMessage}
                    </div>
                  )}
                </div>
              )}

              {isCustomLlm && (
                <div style={{ marginBottom: 20 }}>
                  <label className="font13" style={{ 
                    color: '#4a5568',
                    marginBottom: 8,
                    display: 'block'
                  }}>
                    Base URL <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control font13"
                    placeholder="Enter base URL"
                    value={node.data.baseUrl || ''}
                    onChange={(e) => onChange({ ...node.data, baseUrl: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      fontSize: 13,
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                    }}
                  />
                </div>
              )}

              {/* API Key Input — not used for Custom LLM */}
              {!isCustomLlm && (
              <div>
                <label
                  className="font13"
                  style={{
                    color: '#4a5568',
                    marginBottom: 8,
                    display: 'block'
                  }}
                >
                  API Key
                </label>

                <div
                  style={{
                    position: 'relative',
                    width: '100%'
                  }}
                >
                  <input
                    type={node.data.showApiKey ? 'text' : 'password'}
                    className="form-control font13"
                    placeholder="Enter your API key"
                    value={node.data.apiKey || ''}
                    onChange={(e) =>
                      onChange({
                        ...node.data,
                        apiKey: e.target.value
                      })
                    }
                    style={{
                      height: 44,
                      width: '100%',
                      padding: '10px 44px 10px 12px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: 8,
                      fontSize: 13,
                      lineHeight: '22px',
                      boxSizing: 'border-box',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      transition: 'all 0.2s ease'
                    }}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...node.data,
                        showApiKey: !node.data.showApiKey
                      })
                    }
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 24,
                      width: 24
                    }}
                  >
                    <i
                      className={`fa fa-eye${node.data.showApiKey ? '-slash' : ''}`}
                      style={{
                        fontSize: 16,
                        color: '#6b7280'
                      }}
                    />
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>

          {!isCustomLlm && (
          <>
          {/* Advanced Settings Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: 16,
              color: '#2d3748',
              fontSize: 13,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              marginBottom: showAdvanced ? 16 : 0,
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fa-solid fa-sliders" style={{ color: '#4880FF' }}></i>
              Advanced Settings
            </div>
            <i 
              className={`fa-solid fa-chevron-${showAdvanced ? 'up' : 'down'}`} 
              style={{ 
                color: '#4880FF',
                transition: 'transform 0.2s ease'
              }}
            />
          </button>

          {/* Advanced Settings Content for LLM Configuration */}
          <div style={{
            maxHeight: showAdvanced ? '500px' : '0',
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            opacity: showAdvanced ? 1 : 0
          }}>
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: 16,
              padding: '10px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              {/* Base URL */}
              <div style={{ marginBottom: 20 }}>
                <label className="font13" style={{ 
                  color: '#4a5568',
                  marginBottom: 8,
                  display: 'block'
                }}>
                  Base URL
                </label>
                <input
                  type="text"
                  className={`form-control font13${node.data.errorMessage && node.data.errorMessage.toLowerCase().includes('base_url') ? ' is-invalid' : ''}`}
                  placeholder="Enter base URL"
                  value={node.data.baseUrl || ''}
                  onChange={(e) => onChange({ ...node.data, baseUrl: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                  }}
                />
                 {node.data.errorMessage && node.data.errorMessage.toLowerCase().includes('base_url') && (
                    <div className="invalid-feedback" style={{ color: '#dc3545', fontSize: 13, marginTop: 4 }}>
                        {node.data.errorMessage}
                    </div>
                )}
              </div>

              {/* API Version */}
              <div style={{ marginBottom: 20 }}>
                <label className="font13" style={{ 
                  color: '#4a5568',
                  marginBottom: 8,
                  display: 'block'
                }}>
                  API Version
                </label>
                <input
                  type="text"
                  className="form-control font13"
                  placeholder="Enter API version"
                  value={node.data.apiVersion || ''}
                  onChange={(e) => onChange({ ...node.data, apiVersion: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                  }}
                />
              </div>

              {/* Async Mode */}
              <div style={{ marginBottom: 20 }}>
                <label className="font13" style={{ 
                  color: '#4a5568',
                  marginBottom: 8,
                  display: 'block'
                }}>
                  Async Mode
                </label>
                <input
                  type="text"
                  className="form-control font13"
                  placeholder="Enter async mode"
                  value={node.data.asyncMode || ''}
                  onChange={(e) => onChange({ ...node.data, asyncMode: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                  }}
                />
              </div>

              {/* Embed Model */}
              <div>
                <label className="font13" style={{ 
                  color: '#4a5568',
                  marginBottom: 8,
                  display: 'block'
                }}>
                  Embed Model
                </label>
                <input
                  type="text"
                  className="form-control font13"
                  placeholder="Enter embed model"
                  value={node.data.embedModel || ''}
                  onChange={(e) => onChange({ ...node.data, embedModel: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                  }}
                />
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    );
  }


// LLM Configuration

  return null;
} 